import DataBase.DBQuery
import DataBase.NKDBInterface
import Structs.*
import Structs.Api.ApiConfig
import Structs.Api.ApiNote
import Utils.DatesManipulator
import Utils.OrderStatus
import Utils.TaxNoteSum

/**
 * Restores the JavaScript-visible contract that nexilcolab relied on with the
 * LEGACY Kotlin/JS backend. The IR backend mangles members that are not
 * annotated with @JsName, so plain property reads/writes from TS code
 * (e.g. `note.agent_id`, `pair.first`, `list.size`) would break.
 *
 * Each shim installs a clean-named accessor on the class prototype that
 * delegates to the (mangled) Kotlin accessor via typed Kotlin code, so the
 * compiler resolves the real member regardless of the mangling scheme.
 */

private fun defGet(proto: dynamic, name: String, getter: (dynamic) -> Any?) {
    js("Object.defineProperty(proto, name, { configurable: true, get: function () { return getter(this); } })")
}

/**
 * Prototype getter+setter for TS. Never use this (or [defGet]) for a Kotlin
 * `var` that already has `@myName`/`@JsName` of the same JS name — IR compiles
 * that accessor to `this.<name> = v`, so the shim recurses until the stack
 * overflows (OrderNote.row_version during GET_COFC_ORDER deserialize).
 */
private fun defAcc(proto: dynamic, name: String, getter: (dynamic) -> Any?, setter: (dynamic, dynamic) -> Unit) {
    js("Object.defineProperty(proto, name, { configurable: true, get: function () { return getter(this); }, set: function (v) { setter(this, v); } })")
}

private fun defMethod1(proto: dynamic, name: String, impl: (dynamic, dynamic) -> Any?) {
    js("if (proto[name] === undefined) { proto[name] = function (a) { return impl(this, a); }; }")
}

private fun defMethod0(proto: dynamic, name: String, impl: (dynamic) -> Any?) {
    js("if (proto[name] === undefined) { proto[name] = function () { return impl(this); }; }")
}

private fun defMethod2(proto: dynamic, name: String, impl: (dynamic, dynamic, dynamic) -> Any?) {
    js("if (proto[name] === undefined) { proto[name] = function (a, b) { return impl(this, a, b); }; }")
}

/** Lets TS pass the identity mode as 'AUTO' / 'NETWORK' / 'BRANCH', or omit it. */
private fun toBusinessIdentityMode(v: dynamic): BusinessIdentityMode =
    when (v?.toString()?.uppercase()) {
        "NETWORK" -> BusinessIdentityMode.NETWORK
        "BRANCH" -> BusinessIdentityMode.BRANCH
        else -> BusinessIdentityMode.AUTO
    }

private fun dateOrNow(v: dynamic): String =
    v?.toString()?.takeIf { it.isNotEmpty() } ?: DatesManipulator.dateNow()

private fun <T : Any> protoOf(c: JsClass<T>): dynamic = c.asDynamic().prototype

private fun jsProtoOf(o: Any): dynamic = js("Object").getPrototypeOf(o)

private fun installStdlibShims() {
    // kotlin.Pair / kotlin.Triple — web reads result.first / .second / .third
    val pairProto = protoOf(Pair::class.js)
    defGet(pairProto, "first") { it.unsafeCast<Pair<Any?, Any?>>().first }
    defGet(pairProto, "second") { it.unsafeCast<Pair<Any?, Any?>>().second }
    val tripleProto = protoOf(Triple::class.js)
    defGet(tripleProto, "first") { it.unsafeCast<Triple<Any?, Any?, Any?>>().first }
    defGet(tripleProto, "second") { it.unsafeCast<Triple<Any?, Any?, Any?>>().second }
    defGet(tripleProto, "third") { it.unsafeCast<Triple<Any?, Any?, Any?>>().third }

    // kotlin.Enum — web reads e.g. result.second.name !== 'SUCCESS'
    val enumProto = jsProtoOf(jsProtoOf(DBQuery.SUCCESS))
    defGet(enumProto, "name") { it.unsafeCast<Enum<*>>().name }
    defGet(enumProto, "ordinal") { it.unsafeCast<Enum<*>>().ordinal }

    // Kotlin collections — web calls .toArray(), reads .size, calls .get(i)
    val arrayListProto = protoOf(ArrayList::class.js)
    defGet(arrayListProto, "size") { it.unsafeCast<ArrayList<Any?>>().size }
    defMethod1(arrayListProto, "get") { self, i -> self.unsafeCast<ArrayList<Any?>>()[i.unsafeCast<Int>()] }

    val emptyListProto = jsProtoOf(emptyList<Any>())
    defGet(emptyListProto, "size") { 0 }
    defMethod0(emptyListProto, "toArray") { js("[]") }
    defMethod1(emptyListProto, "get") { _, _ -> null }

    // Kotlin maps — defensive: .get(key) for map results
    val hashMapProto = protoOf(HashMap::class.js)
    defMethod1(hashMapProto, "get") { self, k -> self.unsafeCast<HashMap<Any?, Any?>>()[k] }
    val linkedMapProto = protoOf(LinkedHashMap::class.js)
    defMethod1(linkedMapProto, "get") { self, k -> self.unsafeCast<LinkedHashMap<Any?, Any?>>()[k] }
}

private fun installNoteShims(proto: dynamic) {
    defGet(proto, "date") { it.unsafeCast<Note>().date }
    defGet(proto, "date_issued") { it.unsafeCast<Note>().date_issued }
    defGet(proto, "agent") { it.unsafeCast<Note>().agent }
    defGet(proto, "id") { it.unsafeCast<Note>().id }
    defGet(proto, "ent_id") { it.unsafeCast<Note>().ent_id }
    defAcc(proto, "active", { it.unsafeCast<Note>().active }, { s, v -> s.unsafeCast<Note>().active = v.unsafeCast<Int>() })
    defAcc(proto, "notes", { it.unsafeCast<Note>().notes }, { s, v -> s.unsafeCast<Note>().notes = v.unsafeCast<String>() })
    defAcc(proto, "agent_id", { it.unsafeCast<Note>().agent_id }, { s, v -> s.unsafeCast<Note>().agent_id = v.unsafeCast<Int>() })
    defAcc(proto, "notes2", { it.unsafeCast<Note>().notes2 }, { s, v -> s.unsafeCast<Note>().notes2 = v.unsafeCast<String>() })
    defAcc(proto, "notes3", { it.unsafeCast<Note>().notes3 }, { s, v -> s.unsafeCast<Note>().notes3 = v.unsafeCast<String>() })
    defAcc(proto, "notes4", { it.unsafeCast<Note>().notes4 }, { s, v -> s.unsafeCast<Note>().notes4 = v.unsafeCast<String>() })
    defAcc(proto, "notes5", { it.unsafeCast<Note>().notes5 }, { s, v -> s.unsafeCast<Note>().notes5 = v.unsafeCast<String>() })
    defAcc(proto, "action_time", { it.unsafeCast<Note>().action_time }, { s, v -> s.unsafeCast<Note>().action_time = v.unsafeCast<String>() })
    defAcc(proto, "driver_id", { it.unsafeCast<Note>().driver_id }, { s, v -> s.unsafeCast<Note>().driver_id = v.unsafeCast<Int?>() })
    defAcc(proto, "collector_id", { it.unsafeCast<Note>().collector_id }, { s, v -> s.unsafeCast<Note>().collector_id = v.unsafeCast<Int?>() })
    defAcc(proto, "value", { it.unsafeCast<Note>().value }, { s, v -> s.unsafeCast<Note>().value = v.unsafeCast<Float>() })
    defAcc(proto, "taxPaid", { it.unsafeCast<Note>().taxPaid }, { s, v -> s.unsafeCast<Note>().taxPaid = v.unsafeCast<Float>() })
    defAcc(proto, "amount_pos", { it.unsafeCast<Note>().amount_pos }, { s, v -> s.unsafeCast<Note>().amount_pos = v.unsafeCast<Float>() })
    defAcc(proto, "amount_pos_sec", { it.unsafeCast<Note>().amount_pos_sec }, { s, v -> s.unsafeCast<Note>().amount_pos_sec = v.unsafeCast<Float>() })
    defAcc(proto, "amount_neg", { it.unsafeCast<Note>().amount_neg }, { s, v -> s.unsafeCast<Note>().amount_neg = v.unsafeCast<Float>() })
    defAcc(proto, "amount_neg_sec", { it.unsafeCast<Note>().amount_neg_sec }, { s, v -> s.unsafeCast<Note>().amount_neg_sec = v.unsafeCast<Float>() })
    defAcc(proto, "discount", { it.unsafeCast<Note>().discount }, { s, v -> s.unsafeCast<Note>().discount = v.unsafeCast<Float>() })
    defAcc(proto, "delivery_info", { it.unsafeCast<Note>().delivery_info }, { s, v -> s.unsafeCast<Note>().delivery_info = v.unsafeCast<MutableList<ProductDelivery>>() })
    defAcc(proto, "connected_id", { it.unsafeCast<Note>().connected_id }, { s, v -> s.unsafeCast<Note>().connected_id = v.unsafeCast<Long>() })
    defAcc(proto, "histroy_info", { it.unsafeCast<Note>().histroy_info }, { s, v -> s.unsafeCast<Note>().histroy_info = v.unsafeCast<MutableList<ProductDelivery>>() })
    // Forced static_data helpers (כתובת / שם עסק מאולצים) used by DocumentPreview + order datasheet.
    defMethod0(proto, "getStaticData") { it.unsafeCast<Note>().getStaticData() }
    defMethod0(proto, "hasStaticData") { it.unsafeCast<Note>().hasStaticData() }
    defMethod1(proto, "effectiveAddress") { self, fallback ->
        self.unsafeCast<Note>().effectiveAddress(fallback?.toString() ?: "")
    }
    defMethod1(proto, "effectiveBusinessName") { self, fallback ->
        self.unsafeCast<Note>().effectiveBusinessName(fallback?.toString() ?: "")
    }
    defMethod1(proto, "effectiveBusinessId") { self, fallback ->
        self.unsafeCast<Note>().effectiveBusinessId(fallback?.toString() ?: "")
    }
    defMethod1(proto, "effectivePhone") { self, fallback ->
        self.unsafeCast<Note>().effectivePhone(fallback?.toString() ?: "")
    }
}

private fun installDocShims() {
    val dvProto = protoOf(DeliveryNote::class.js)
    installNoteShims(dvProto)
    defGet(dvProto, "order_id") { it.unsafeCast<DeliveryNote>().order_id }
    defGet(dvProto, "pdf") { it.unsafeCast<DeliveryNote>().pdf }

    val onProto = protoOf(OrderNote::class.js)
    installNoteShims(onProto)
    defGet(onProto, "order_id") { it.unsafeCast<OrderNote>().order_id }
    defAcc(onProto, "notes6", { it.unsafeCast<OrderNote>().notes6 }, { s, v -> s.unsafeCast<OrderNote>().notes6 = v.unsafeCast<String>() })
    defAcc(onProto, "notes7", { it.unsafeCast<OrderNote>().notes7 }, { s, v -> s.unsafeCast<OrderNote>().notes7 = v.unsafeCast<String>() })
    defAcc(onProto, "notes8", { it.unsafeCast<OrderNote>().notes8 }, { s, v -> s.unsafeCast<OrderNote>().notes8 = v.unsafeCast<String>() })
    defAcc(onProto, "notes9", { it.unsafeCast<OrderNote>().notes9 }, { s, v -> s.unsafeCast<OrderNote>().notes9 = v.unsafeCast<String>() })
    defAcc(onProto, "notes10", { it.unsafeCast<OrderNote>().notes10 }, { s, v -> s.unsafeCast<OrderNote>().notes10 = v.unsafeCast<String>() })
    // row_version is @myName("row_version") — already a stable JS field. Do not defAcc it.
    defGet(onProto, "e_agent") { it.unsafeCast<OrderNote>().e_agent }
    defGet(onProto, "ref_id") { it.unsafeCast<OrderNote>().ref_id }
    defGet(onProto, "category") { it.unsafeCast<OrderNote>().category }
    defGet(onProto, "e_agent") { it.unsafeCast<OrderNote>().e_agent }
    defMethod0(onProto, "getDeliveryDate") { it.unsafeCast<OrderNote>().getDeliveryDate() }
    defMethod0(onProto, "rowVersion") { it.unsafeCast<OrderNote>().rowVersion() }

    // Tax notes: base props document_date/id/client_id/value/value_left/total_value/increase_id are
    // already @myName'd in ClientLightTaxNote; add the rest (pdf is also @myName'd, shim it anyway).
    fun taxNoteBase(proto: dynamic) {
        defGet(proto, "type") { it.unsafeCast<ClientLightTaxNote>().type }
        defGet(proto, "pdf") { it.unsafeCast<ClientLightTaxNote>().pdf }
        defGet(proto, "action_time") { it.unsafeCast<ClientLightTaxNote>().action_time }
        defGet(proto, "taxConfirmation") { it.unsafeCast<ClientLightTaxNote>().taxConfirmation }
        defGet(proto, "discount_percent") { it.unsafeCast<ClientLightTaxNote>().discount_percent }
        defAcc(proto, "signer", { it.unsafeCast<ClientLightTaxNote>().signer }, { s, v -> s.unsafeCast<ClientLightTaxNote>().signer = v.unsafeCast<String?>() })
        defAcc(proto, "date_sign", { it.unsafeCast<ClientLightTaxNote>().date_sign }, { s, v -> s.unsafeCast<ClientLightTaxNote>().date_sign = v.unsafeCast<String?>() })
        defGet(proto, "taxNoteType") { it.unsafeCast<ClientLightTaxNote>().taxNoteType }
        defAcc(proto, "isClient", { it.unsafeCast<ClientLightTaxNote>().isClient }, { s, v -> s.unsafeCast<ClientLightTaxNote>().isClient = v.unsafeCast<Boolean>() })
        defMethod0(proto, "supplierInvoiceNumber") { it.unsafeCast<ClientLightTaxNote>().supplierInvoiceNumber() }
    }

    val ctnProto = protoOf(ClientTaxNote::class.js)
    taxNoteBase(ctnProto)
    defGet(ctnProto, "date") { it.unsafeCast<ClientTaxNote>().date }
    defGet(ctnProto, "agent") { it.unsafeCast<ClientTaxNote>().agent }
    defGet(ctnProto, "order_id") { it.unsafeCast<ClientTaxNote>().order_id }
    defGet(ctnProto, "driver_id") { it.unsafeCast<ClientTaxNote>().driver_id }
    defGet(ctnProto, "collector_id") { it.unsafeCast<ClientTaxNote>().collector_id }
    defAcc(ctnProto, "agent_id", { it.unsafeCast<ClientTaxNote>().agent_id }, { s, v -> s.unsafeCast<ClientTaxNote>().agent_id = v.unsafeCast<Int>() })
    defAcc(ctnProto, "notes2", { it.unsafeCast<ClientTaxNote>().notes2 }, { s, v -> s.unsafeCast<ClientTaxNote>().notes2 = v.unsafeCast<String>() })
    defAcc(ctnProto, "notes3", { it.unsafeCast<ClientTaxNote>().notes3 }, { s, v -> s.unsafeCast<ClientTaxNote>().notes3 = v.unsafeCast<String>() })
    defAcc(ctnProto, "notes4", { it.unsafeCast<ClientTaxNote>().notes4 }, { s, v -> s.unsafeCast<ClientTaxNote>().notes4 = v.unsafeCast<String>() })
    defAcc(ctnProto, "notes5", { it.unsafeCast<ClientTaxNote>().notes5 }, { s, v -> s.unsafeCast<ClientTaxNote>().notes5 = v.unsafeCast<String>() })
    defAcc(ctnProto, "details", { it.unsafeCast<ClientTaxNote>().details }, { s, v -> s.unsafeCast<ClientTaxNote>().details = v.unsafeCast<String>() })
    defAcc(ctnProto, "cover_dates", { it.unsafeCast<ClientTaxNote>().cover_dates }, { s, v -> s.unsafeCast<ClientTaxNote>().cover_dates = v.unsafeCast<String>() })
    defAcc(ctnProto, "external_details", { it.unsafeCast<ClientTaxNote>().external_details }, { s, v -> s.unsafeCast<ClientTaxNote>().external_details = v.unsafeCast<String>() })
    defAcc(ctnProto, "paymentsData", { it.unsafeCast<ClientTaxNote>().paymentsData }, { s, v -> s.unsafeCast<ClientTaxNote>().paymentsData = v.unsafeCast<List<PaymentData>>() })
    defMethod0(ctnProto, "getStaticData") { it.unsafeCast<ClientTaxNote>().getStaticData() }
    defMethod0(ctnProto, "supplierInvoiceNumber") { it.unsafeCast<ClientTaxNote>().supplierInvoiceNumber() }
    defMethod0(ctnProto, "supplierInputEquipment") { it.unsafeCast<ClientTaxNote>().supplierInputEquipment() }

    taxNoteBase(protoOf(ClientLightTaxNoteImp::class.js))

    fun payBase(proto: dynamic) {
        defGet(proto, "date") { it.unsafeCast<ClientLightPay>().date }
        defGet(proto, "action_time") { it.unsafeCast<ClientLightPay>().action_time }
        defGet(proto, "ref_id") { it.unsafeCast<ClientLightPay>().ref_id }
        defAcc(proto, "isClient", { it.unsafeCast<ClientLightPay>().isClient }, { s, v -> s.unsafeCast<ClientLightPay>().isClient = v.unsafeCast<Boolean>() })
    }

    val cpProto = protoOf(ClientPay::class.js)
    payBase(cpProto)
    defGet(cpProto, "agent") { it.unsafeCast<ClientPay>().agent }
    defGet(cpProto, "driver_id") { it.unsafeCast<ClientPay>().driver_id }
    defGet(cpProto, "active") { it.unsafeCast<ClientPay>().active }
    defGet(cpProto, "details") { it.unsafeCast<ClientPay>().details }
    defAcc(cpProto, "taxNotes", { it.unsafeCast<ClientPay>().taxNotes }, { s, v -> s.unsafeCast<ClientPay>().taxNotes = v.unsafeCast<MutableList<Int>>() })
    defMethod0(cpProto, "displayTaxNoteIds") { it.unsafeCast<ClientPay>().displayTaxNoteIds() }
    defAcc(cpProto, "tax_note_id", { it.unsafeCast<ClientPay>().tax_note_id }, { s, v -> s.unsafeCast<ClientPay>().tax_note_id = v.unsafeCast<String>() })
    defAcc(cpProto, "external_details", { it.unsafeCast<ClientPay>().external_details }, { s, v -> s.unsafeCast<ClientPay>().external_details = v.unsafeCast<String>() })
    defGet(cpProto, "paymentsData") { it.unsafeCast<ClientPay>().paymentsData }

    val clpProto = protoOf(ClientLightPayImp::class.js)
    payBase(clpProto)
    defGet(clpProto, "tax_note_id") { it.unsafeCast<ClientLightPayImp>().tax_note_id }

    val ccProto = protoOf(ClientCarteset::class.js)
    defGet(ccProto, "date") { it.unsafeCast<ClientCarteset>().date }
    defGet(ccProto, "number") { it.unsafeCast<ClientCarteset>().number }
    defGet(ccProto, "id") { it.unsafeCast<ClientCarteset>().id }
    defGet(ccProto, "positive") { it.unsafeCast<ClientCarteset>().positive }
    defGet(ccProto, "negative") { it.unsafeCast<ClientCarteset>().negative }
    defGet(ccProto, "tax_paid") { it.unsafeCast<ClientCarteset>().tax_paid }
    defGet(ccProto, "state") { it.unsafeCast<ClientCarteset>().state }
    defGet(ccProto, "order_id") { it.unsafeCast<ClientCarteset>().order_id }
    defGet(ccProto, "notes") { it.unsafeCast<ClientCarteset>().notes }
    defAcc(ccProto, "isClient", { it.unsafeCast<ClientCarteset>().isClient }, { s, v -> s.unsafeCast<ClientCarteset>().isClient = v.unsafeCast<Boolean>() })

    val mcProto = protoOf(MonthlyCycle::class.js)
    defGet(mcProto, "date") { it.unsafeCast<MonthlyCycle>().date }
    defGet(mcProto, "id") { it.unsafeCast<MonthlyCycle>().id }
    defAcc(mcProto, "value", { it.unsafeCast<MonthlyCycle>().value }, { s, v -> s.unsafeCast<MonthlyCycle>().value = v.unsafeCast<Float>() })
    defAcc(mcProto, "paid", { it.unsafeCast<MonthlyCycle>().paid }, { s, v -> s.unsafeCast<MonthlyCycle>().paid = v.unsafeCast<Float>() })
    defAcc(mcProto, "openValue", { it.unsafeCast<MonthlyCycle>().openValue }, { s, v -> s.unsafeCast<MonthlyCycle>().openValue = v.unsafeCast<Float?>() })

    val ibProto = protoOf(InformationBase::class.js)
    // Do not defineProperty("apis"): @myName("apis") already has get+set on a
    // backing field. A shim named `apis` aliases that setter and recurses
    // during InformationBase init / document decode.
    defMethod0(ibProto, "getExternalSoftwareNote") { it.unsafeCast<InformationBase>().getExternalSoftwareNote() }
    defMethod0(ibProto, "isSentToExternalSoftware") { it.unsafeCast<InformationBase>().isSentToExternalSoftware() }
    defMethod0(ibProto, "photoCount") { it.unsafeCast<InformationBase>().photoCount() }
    defMethod0(ibProto, "digiPhoto") { it.unsafeCast<InformationBase>().digiPhoto() }
    defMethod0(ibProto, "displayDocId") { it.unsafeCast<InformationBase>().displayDocId() }

    val anProto = protoOf(ApiNote::class.js)
    defGet(anProto, "api_method") { it.unsafeCast<ApiNote>().api_method }
    defGet(anProto, "doc_type") { it.unsafeCast<ApiNote>().doc_type }
    defGet(anProto, "doc_id") { it.unsafeCast<ApiNote>().doc_id }
    defGet(anProto, "ent_id") { it.unsafeCast<ApiNote>().ent_id }
    defGet(anProto, "doc_date") { it.unsafeCast<ApiNote>().doc_date }
    defGet(anProto, "send") { it.unsafeCast<ApiNote>().send }
    defGet(anProto, "reason") { it.unsafeCast<ApiNote>().reason }
    defGet(anProto, "retries") { it.unsafeCast<ApiNote>().retries }
    defGet(anProto, "action_time") { it.unsafeCast<ApiNote>().action_time }

    val vnProto = protoOf(VisitNote::class.js)
    defGet(vnProto, "comment") { it.unsafeCast<VisitNote>().comment }
    defGet(vnProto, "date_issued") { it.unsafeCast<VisitNote>().date_issued }
    defGet(vnProto, "agent") { it.unsafeCast<VisitNote>().agent }
    defGet(vnProto, "value") { it.unsafeCast<VisitNote>().value }
    defGet(vnProto, "order_id") { it.unsafeCast<VisitNote>().order_id }
    defGet(vnProto, "driver_id") { it.unsafeCast<VisitNote>().driver_id }

    defGet(protoOf(EmptyDoc::class.js), "value") { it.unsafeCast<EmptyDoc>().value }
    defAcc(protoOf(Debt::class.js), "type", { it.unsafeCast<Debt>().type }, { s, v -> s.unsafeCast<Debt>().type = v.unsafeCast<Int?>() })

    val nshProto = protoOf(NoteSignHolder::class.js)
    defGet(nshProto, "note_id") { it.unsafeCast<NoteSignHolder>().note_id }
    defGet(nshProto, "note_type") { it.unsafeCast<NoteSignHolder>().note_type }
    defGet(nshProto, "digi_sign") { it.unsafeCast<NoteSignHolder>().digi_sign }
    defGet(nshProto, "digi_photo") { it.unsafeCast<NoteSignHolder>().digi_photo }
    defGet(nshProto, "signer") { it.unsafeCast<NoteSignHolder>().signer }
    defGet(nshProto, "date") { it.unsafeCast<NoteSignHolder>().date }
}

private fun installEntityShims(proto: dynamic) {
    defGet(proto, "id") { it.unsafeCast<Entity>().id }
    defGet(proto, "agent") { it.unsafeCast<Entity>().agent }
    defGet(proto, "business_name") { it.unsafeCast<Entity>().business_name }
    defGet(proto, "business_id") { it.unsafeCast<Entity>().business_id }
    defGet(proto, "address") { it.unsafeCast<Entity>().address }
    defGet(proto, "print_state") { it.unsafeCast<Entity>().print_state }
    defGet(proto, "date") { it.unsafeCast<Entity>().date }
    defGet(proto, "phone_contact") { it.unsafeCast<Entity>().phone_contact }
    defGet(proto, "category") { it.unsafeCast<Entity>().category }
    defGet(proto, "category2") { it.unsafeCast<Entity>().category2 }
    defGet(proto, "discount") { it.unsafeCast<Entity>().discount }
    defGet(proto, "external_id") { it.unsafeCast<Entity>().external_id }
    defGet(proto, "email") { it.unsafeCast<Entity>().email }
    defGet(proto, "no_tax_client") { it.unsafeCast<Entity>().no_tax_client }
    defGet(proto, "obligo") { it.unsafeCast<Entity>().obligo }
    defGet(proto, "payment_notes") { it.unsafeCast<Entity>().payment_notes }
    defGet(proto, "days_to_pay") { it.unsafeCast<Entity>().days_to_pay }
    defGet(proto, "price_control") { it.unsafeCast<Entity>().price_control }
    defGet(proto, "include_tax") { it.unsafeCast<Entity>().include_tax }
    defGet(proto, "tax_note_type") { it.unsafeCast<Entity>().tax_note_type }
    defGet(proto, "comments") { it.unsafeCast<Entity>().comments }
    defGet(proto, "notes2") { it.unsafeCast<Entity>().notes2 }
    defGet(proto, "notes3") { it.unsafeCast<Entity>().notes3 }
    defGet(proto, "notes4") { it.unsafeCast<Entity>().notes4 }
    defGet(proto, "notes5") { it.unsafeCast<Entity>().notes5 }
    defGet(proto, "notes6") { it.unsafeCast<Entity>().notes6 }
    defGet(proto, "notes7") { it.unsafeCast<Entity>().notes7 }
    defGet(proto, "notes8") { it.unsafeCast<Entity>().notes8 }
    defGet(proto, "notes9") { it.unsafeCast<Entity>().notes9 }
    defGet(proto, "notes10") { it.unsafeCast<Entity>().notes10 }
    defGet(proto, "notes11") { it.unsafeCast<Entity>().notes11 }
    defGet(proto, "notes12") { it.unsafeCast<Entity>().notes12 }
    defGet(proto, "name_english") { it.unsafeCast<Entity>().name_english }
    defGet(proto, "address_english") { it.unsafeCast<Entity>().address_english }
    defGet(proto, "cc_token") { it.unsafeCast<Entity>().cc_token }
    defGet(proto, "cc_expiry") { it.unsafeCast<Entity>().cc_expiry }
    defAcc(proto, "date_contact", { it.unsafeCast<Entity>().date_contact }, { s, v -> s.unsafeCast<Entity>().date_contact = v.unsafeCast<String?>() })
    defGet(proto, "debtWall") { it.unsafeCast<Entity>().debtWall }
    defGet(proto, "masterBranch") { it.unsafeCast<Entity>().masterBranch }
    defGet(proto, "lastVisit") { it.unsafeCast<Entity>().lastVisit }
    defAcc(proto, "position", { it.unsafeCast<Entity>().position }, { s, v -> s.unsafeCast<Entity>().position = v.unsafeCast<Int?>() })
    defAcc(proto, "location", { it.unsafeCast<Entity>().location }, { s, v -> s.unsafeCast<Entity>().location = v.unsafeCast<String?>() })
    defAcc(proto, "active", { it.unsafeCast<Entity>().active }, { s, v -> s.unsafeCast<Entity>().active = v.unsafeCast<Int>() })
    defAcc(proto, "phone", { it.unsafeCast<Entity>().phone }, { s, v -> s.unsafeCast<Entity>().phone = v.unsafeCast<String?>() })
    defAcc(proto, "branch", { it.unsafeCast<Entity>().branch }, { s, v -> s.unsafeCast<Entity>().branch = v.unsafeCast<Int>() })
    defAcc(proto, "master", { it.unsafeCast<Entity>().master }, { s, v -> s.unsafeCast<Entity>().master = v.unsafeCast<Int>() })
    defAcc(proto, "city", { it.unsafeCast<Entity>().city }, { s, v -> s.unsafeCast<Entity>().city = v.unsafeCast<String>() })
    defAcc(proto, "street", { it.unsafeCast<Entity>().street }, { s, v -> s.unsafeCast<Entity>().street = v.unsafeCast<String>() })
    defAcc(proto, "streetNumber", { it.unsafeCast<Entity>().streetNumber }, { s, v -> s.unsafeCast<Entity>().streetNumber = v.unsafeCast<String>() })
    // Own-row identity, never resolved through a network master.
    defMethod1(proto, "getOwnBusinessName") { self, date ->
        self.unsafeCast<Entity>().getOwnBusinessName(dateOrNow(date))
    }
    defMethod1(proto, "getOwnBusinessId") { self, date ->
        self.unsafeCast<Entity>().getOwnBusinessId(dateOrNow(date))
    }
}

private fun installEntityClassShims() {
    val clientProto = protoOf(Client::class.js)
    installEntityShims(clientProto)
    defGet(clientProto, "days") { it.unsafeCast<Client>().days }
    defGet(clientProto, "notes") { it.unsafeCast<Client>().notes }
    defGet(clientProto, "min_order") { it.unsafeCast<Client>().min_order }
    defGet(clientProto, "min_order_sum") { it.unsafeCast<Client>().min_order_sum }
    defGet(clientProto, "available_control") { it.unsafeCast<Client>().available_control }
    defGet(clientProto, "cofc_lock") { it.unsafeCast<Client>().cofc_lock }
    defGet(clientProto, "arrival_instructions") { it.unsafeCast<Client>().arrival_instructions }
    defGet(clientProto, "card_image") { it.unsafeCast<Client>().card_image }
    defGet(clientProto, "return_pct") { it.unsafeCast<Client>().return_pct }
    defMethod0(clientProto, "hasArrivalCard") { it.unsafeCast<Client>().hasArrivalCard() }
    defGet(clientProto, "creation_time") { it.unsafeCast<Client>().creation_time }
    defGet(clientProto, "edi") { it.unsafeCast<Client>().edi }
    defGet(clientProto, "from_hour") { it.unsafeCast<Client>().from_hour }
    defGet(clientProto, "to_hour") { it.unsafeCast<Client>().to_hour }
    defGet(clientProto, "link") { it.unsafeCast<Client>().link }
    defGet(clientProto, "net_split") { it.unsafeCast<Client>().net_split }
    defAcc(clientProto, "name", { it.unsafeCast<Client>().name }, { s, v -> s.unsafeCast<Client>().name = v.unsafeCast<String>() })
    // Network billing mode + business identity resolution. All of these read net_split
    // from the master, so the web never has to decide which row to look at.
    defMethod0(clientProto, "networkBillingMode") { it.unsafeCast<Client>().networkBillingMode() }
    defMethod0(clientProto, "splitsNetworkInvoices") { it.unsafeCast<Client>().splitsNetworkInvoices() }
    defMethod0(clientProto, "billsBranchIdentity") { it.unsafeCast<Client>().billsBranchIdentity() }
    defMethod1(clientProto, "getNetworkBusinessName") { self, date ->
        self.unsafeCast<Client>().getNetworkBusinessName(dateOrNow(date))
    }
    defMethod1(clientProto, "getNetworkBusinessId") { self, date ->
        self.unsafeCast<Client>().getNetworkBusinessId(dateOrNow(date))
    }
    defMethod1(clientProto, "hasValidOwnBusinessIdentity") { self, date ->
        self.unsafeCast<Client>().hasValidOwnBusinessIdentity(dateOrNow(date))
    }
    defMethod2(clientProto, "buildStaticData") { self, date, mode ->
        self.unsafeCast<Client>().buildStaticData(dateOrNow(date), toBusinessIdentityMode(mode))
    }

    val supplierProto = protoOf(Supplier::class.js)
    installEntityShims(supplierProto)
    defAcc(supplierProto, "name", { it.unsafeCast<Supplier>().name }, { s, v -> s.unsafeCast<Supplier>().name = v.unsafeCast<String>() })
    installEntityShims(protoOf(EmptyEnt::class.js))

    val userProto = protoOf(User::class.js)
    defAcc(userProto, "company_id", { it.unsafeCast<User>().company_id }, { s, v -> s.unsafeCast<User>().company_id = v.unsafeCast<Int>() })
    defAcc(userProto, "user_company_name", { it.unsafeCast<User>().user_company_name }, { s, v -> s.unsafeCast<User>().user_company_name = v.unsafeCast<String>() })
    defAcc(userProto, "user_company_print_name", { it.unsafeCast<User>().user_company_print_name }, { s, v -> s.unsafeCast<User>().user_company_print_name = v.unsafeCast<String>() })
    defAcc(userProto, "user_line_number", { it.unsafeCast<User>().user_line_number }, { s, v -> s.unsafeCast<User>().user_line_number = v.unsafeCast<String>() })
    defAcc(userProto, "user_phone_number", { it.unsafeCast<User>().user_phone_number }, { s, v -> s.unsafeCast<User>().user_phone_number = v.unsafeCast<String>() })
    defAcc(userProto, "fax_number", { it.unsafeCast<User>().fax_number }, { s, v -> s.unsafeCast<User>().fax_number = v.unsafeCast<String>() })
    defAcc(userProto, "user_address", { it.unsafeCast<User>().user_address }, { s, v -> s.unsafeCast<User>().user_address = v.unsafeCast<String>() })
    defAcc(userProto, "user_buisness_number", { it.unsafeCast<User>().user_buisness_number }, { s, v -> s.unsafeCast<User>().user_buisness_number = v.unsafeCast<String>() })
    defAcc(userProto, "email", { it.unsafeCast<User>().email }, { s, v -> s.unsafeCast<User>().email = v.unsafeCast<String>() })
    defAcc(userProto, "print_state", { it.unsafeCast<User>().print_state }, { s, v -> s.unsafeCast<User>().print_state = v.unsafeCast<Int>() })
    defAcc(userProto, "default_print_state", { it.unsafeCast<User>().default_print_state }, { s, v -> s.unsafeCast<User>().default_print_state = v.unsafeCast<Int>() })
    defAcc(userProto, "no_tax_p", { it.unsafeCast<User>().no_tax_p }, { s, v -> s.unsafeCast<User>().no_tax_p = v.unsafeCast<Int>() })
    defAcc(userProto, "no_tax_c", { it.unsafeCast<User>().no_tax_c }, { s, v -> s.unsafeCast<User>().no_tax_c = v.unsafeCast<Int>() })
    defAcc(userProto, "active", { it.unsafeCast<User>().active }, { s, v -> s.unsafeCast<User>().active = v.unsafeCast<Int>() })
    defAcc(userProto, "company_type", { it.unsafeCast<User>().company_type }, { s, v -> s.unsafeCast<User>().company_type = v.unsafeCast<Int>() })
    defAcc(userProto, "daily_prices", { it.unsafeCast<User>().daily_prices }, { s, v -> s.unsafeCast<User>().daily_prices = v.unsafeCast<Int>() })
    defAcc(userProto, "add_carteset", { it.unsafeCast<User>().add_carteset }, { s, v -> s.unsafeCast<User>().add_carteset = v.unsafeCast<Int>() })
    defAcc(userProto, "note_price", { it.unsafeCast<User>().note_price }, { s, v -> s.unsafeCast<User>().note_price = v.unsafeCast<Int>() })
    defAcc(userProto, "edi_state", { it.unsafeCast<User>().edi_state }, { s, v -> s.unsafeCast<User>().edi_state = v.unsafeCast<Int>() })
    defAcc(userProto, "taklit", { it.unsafeCast<User>().taklit }, { s, v -> s.unsafeCast<User>().taklit = v.unsafeCast<Int>() })
    defAcc(userProto, "weight", { it.unsafeCast<User>().weight }, { s, v -> s.unsafeCast<User>().weight = v.unsafeCast<Int>() })
    defAcc(userProto, "order_cofc", { it.unsafeCast<User>().order_cofc }, { s, v -> s.unsafeCast<User>().order_cofc = v.unsafeCast<Int>() })
    defAcc(userProto, "tax_pay_note", { it.unsafeCast<User>().tax_pay_note }, { s, v -> s.unsafeCast<User>().tax_pay_note = v.unsafeCast<Int>() })
    defAcc(userProto, "barcode_reader", { it.unsafeCast<User>().barcode_reader }, { s, v -> s.unsafeCast<User>().barcode_reader = v.unsafeCast<Int>() })
    defAcc(userProto, "external_id", { it.unsafeCast<User>().external_id }, { s, v -> s.unsafeCast<User>().external_id = v.unsafeCast<Int>() })
    defAcc(userProto, "tax_note_print_state", { it.unsafeCast<User>().tax_note_print_state }, { s, v -> s.unsafeCast<User>().tax_note_print_state = v.unsafeCast<Int>() })
    defAcc(userProto, "unit_drop", { it.unsafeCast<User>().unit_drop }, { s, v -> s.unsafeCast<User>().unit_drop = v.unsafeCast<String>() })
    defAcc(userProto, "tax_note_split", { it.unsafeCast<User>().tax_note_split }, { s, v -> s.unsafeCast<User>().tax_note_split = v.unsafeCast<Int>() })
    defAcc(userProto, "tax_note_split_dv", { it.unsafeCast<User>().tax_note_split_dv }, { s, v -> s.unsafeCast<User>().tax_note_split_dv = v.unsafeCast<Int>() })
    defAcc(userProto, "visit_debt", { it.unsafeCast<User>().visit_debt }, { s, v -> s.unsafeCast<User>().visit_debt = v.unsafeCast<Int>() })
    defAcc(userProto, "hesh_israel", { it.unsafeCast<User>().hesh_israel }, { s, v -> s.unsafeCast<User>().hesh_israel = v.unsafeCast<Int>() })
    defAcc(userProto, "attendance", { it.unsafeCast<User>().attendance }, { s, v -> s.unsafeCast<User>().attendance = v.unsafeCast<Int>() })
    defAcc(userProto, "with_sign", { it.unsafeCast<User>().with_sign }, { s, v -> s.unsafeCast<User>().with_sign = v.unsafeCast<Int>() })

    val agentProto = protoOf(Agent::class.js)
    defAcc(agentProto, "user_name", { it.unsafeCast<Agent>().user_name }, { s, v -> s.unsafeCast<Agent>().user_name = v.unsafeCast<String>() })
    defAcc(agentProto, "user_password", { it.unsafeCast<Agent>().user_password }, { s, v -> s.unsafeCast<Agent>().user_password = v.unsafeCast<String?>() })
    defGet(agentProto, "manager") { it.unsafeCast<Agent>().manager }
    defGet(agentProto, "company_id") { it.unsafeCast<Agent>().company_id }
    defGet(agentProto, "company") { it.unsafeCast<Agent>().company }
    defGet(agentProto, "id") { it.unsafeCast<Agent>().id }
    defGet(agentProto, "print") { it.unsafeCast<Agent>().print }
    defMethod0(agentProto, "forcePricePrint") { it.unsafeCast<Agent>().forcePricePrint() }
    defMethod0(agentProto, "forceSendPrice") { it.unsafeCast<Agent>().forceSendPrice() }
    defMethod0(agentProto, "forceDeliveryNotePrice") { it.unsafeCast<Agent>().forceDeliveryNotePrice() }
    defGet(agentProto, "increase_id") { it.unsafeCast<Agent>().increase_id }
    defGet(agentProto, "car") { it.unsafeCast<Agent>().car }
    defGet(agentProto, "display_name") { it.unsafeCast<Agent>().display_name }
    defGet(agentProto, "phone") { it.unsafeCast<Agent>().phone }
    defGet(agentProto, "active") { it.unsafeCast<Agent>().active }
    defGet(agentProto, "returns") { it.unsafeCast<Agent>().returns }
    defGet(agentProto, "is_agent") { it.unsafeCast<Agent>().is_agent }
    defGet(agentProto, "is_collector") { it.unsafeCast<Agent>().is_collector }
    defGet(agentProto, "is_driver") { it.unsafeCast<Agent>().is_driver }
    defGet(agentProto, "position") { it.unsafeCast<Agent>().position }
    defGet(agentProto, "visit") { it.unsafeCast<Agent>().visit }
    defGet(agentProto, "comment") { it.unsafeCast<Agent>().comment }
    defGet(agentProto, "start_point") { it.unsafeCast<Agent>().start_point }
    defGet(agentProto, "end_point") { it.unsafeCast<Agent>().end_point }
    defGet(agentProto, "show_dashboard") { it.unsafeCast<Agent>().show_dashboard }
    defGet(agentProto, "english") { it.unsafeCast<Agent>().english }
}

private fun installProductShims() {
    val pProto = protoOf(Product::class.js)
    defAcc(pProto, "name", { it.unsafeCast<Product>().name }, { s, v -> s.unsafeCast<Product>().name = v.unsafeCast<String>() })
    defGet(pProto, "id") { it.unsafeCast<Product>().id }
    defGet(pProto, "barcode") { it.unsafeCast<Product>().barcode }
    defGet(pProto, "default_price") { it.unsafeCast<Product>().default_price }
    defAcc(pProto, "position", { it.unsafeCast<Product>().position }, { s, v -> s.unsafeCast<Product>().position = v.unsafeCast<Int>() })
    defAcc(pProto, "active", { it.unsafeCast<Product>().active }, { s, v -> s.unsafeCast<Product>().active = v.unsafeCast<Int>() })
    defGet(pProto, "date") { it.unsafeCast<Product>().date }
    defGet(pProto, "connected_product") { it.unsafeCast<Product>().connected_product }
    defGet(pProto, "no_tax_product") { it.unsafeCast<Product>().no_tax_product }
    defGet(pProto, "taklit_id") { it.unsafeCast<Product>().taklit_id }
    defGet(pProto, "taklit_type") { it.unsafeCast<Product>().taklit_type }
    defGet(pProto, "external_id") { it.unsafeCast<Product>().external_id }
    defGet(pProto, "unit") { it.unsafeCast<Product>().unit }
    defGet(pProto, "unit_amount") { it.unsafeCast<Product>().unit_amount }
    defGet(pProto, "category") { it.unsafeCast<Product>().category }
    defGet(pProto, "category2") { it.unsafeCast<Product>().category2 }
    defGet(pProto, "cost_price") { it.unsafeCast<Product>().cost_price }
    // analytics cost/profit math reads p.cost_include_returns; unshimmed it was
    // undefined -> (1 - undefined) = NaN in רווח / ע.חזרות.
    defGet(pProto, "cost_include_returns") { it.unsafeCast<Product>().cost_include_returns }
    defGet(pProto, "base_price") { it.unsafeCast<Product>().base_price }
    defGet(pProto, "notes2") { it.unsafeCast<Product>().notes2 }
    defGet(pProto, "notes3") { it.unsafeCast<Product>().notes3 }
    defGet(pProto, "notes4") { it.unsafeCast<Product>().notes4 }
    defGet(pProto, "notes5") { it.unsafeCast<Product>().notes5 }
    defGet(pProto, "notes6") { it.unsafeCast<Product>().notes6 }
    // Supplier product's own units (sup_products); client units stay on OrderProduct.
    defGet(pProto, "extra_unit") { it.unsafeCast<Product>().extra_unit }
    defGet(pProto, "conversion_rate") { it.unsafeCast<Product>().conversion_rate }
    defGet(pProto, "third_unit") { it.unsafeCast<Product>().third_unit }
    defGet(pProto, "unit_preferece") { it.unsafeCast<Product>().unit_preferece }
    defAcc(pProto, "isClient", { it.unsafeCast<Product>().isClient }, { s, v -> s.unsafeCast<Product>().isClient = v.unsafeCast<Boolean>() })
    defAcc(pProto, "tempPosition", { it.unsafeCast<Product>().tempPosition }, { s, v -> s.unsafeCast<Product>().tempPosition = v.unsafeCast<Int>() })

    val opProto = protoOf(OrderProduct::class.js)
    defGet(opProto, "id") { it.unsafeCast<OrderProduct>().id }
    defGet(opProto, "product_id") { it.unsafeCast<OrderProduct>().product_id }
    defGet(opProto, "extra_unit") { it.unsafeCast<OrderProduct>().extra_unit }
    defGet(opProto, "conversion_rate") { it.unsafeCast<OrderProduct>().conversion_rate }
    defGet(opProto, "weight") { it.unsafeCast<OrderProduct>().weight }
    defGet(opProto, "volume") { it.unsafeCast<OrderProduct>().volume }
    defGet(opProto, "englishName") { it.unsafeCast<OrderProduct>().englishName }
    defAcc(opProto, "available", { it.unsafeCast<OrderProduct>().available }, { s, v -> s.unsafeCast<OrderProduct>().available = v.unsafeCast<Byte>() })
    defAcc(opProto, "active", { it.unsafeCast<OrderProduct>().active }, { s, v -> s.unsafeCast<OrderProduct>().active = v.unsafeCast<Byte>() })
    defGet(opProto, "orderName") { it.unsafeCast<OrderProduct>().orderName }
    defGet(opProto, "position") { it.unsafeCast<OrderProduct>().position }
    defGet(opProto, "sales") { it.unsafeCast<OrderProduct>().sales }
    defGet(opProto, "forceLock") { it.unsafeCast<OrderProduct>().forceLock }
    defGet(opProto, "hourLock") { it.unsafeCast<OrderProduct>().hourLock }
    defGet(opProto, "dayLock") { it.unsafeCast<OrderProduct>().dayLock }
    defGet(opProto, "step") { it.unsafeCast<OrderProduct>().step }
    defGet(opProto, "collector") { it.unsafeCast<OrderProduct>().collector }
    defGet(opProto, "min_order") { it.unsafeCast<OrderProduct>().min_order }
    defGet(opProto, "armor") { it.unsafeCast<OrderProduct>().armor }
    defGet(opProto, "days_ahead") { it.unsafeCast<OrderProduct>().days_ahead }
    defGet(opProto, "unit_preferece") { it.unsafeCast<OrderProduct>().unit_preferece }
    defGet(opProto, "third_unit") { it.unsafeCast<OrderProduct>().third_unit }
    defGet(opProto, "day_hour_locks") { it.unsafeCast<OrderProduct>().day_hour_locks }
    defGet(opProto, "delivery_cutoff_rules") { it.unsafeCast<OrderProduct>().delivery_cutoff_rules }

    val tpProto = protoOf(TaklitProduct::class.js)
    defGet(tpProto, "name") { it.unsafeCast<TaklitProduct>().name }
    defGet(tpProto, "id") { it.unsafeCast<TaklitProduct>().id }
    defGet(tpProto, "pricea") { it.unsafeCast<TaklitProduct>().pricea }
    defGet(tpProto, "priceb") { it.unsafeCast<TaklitProduct>().priceb }
    defGet(tpProto, "date") { it.unsafeCast<TaklitProduct>().date }

    // PriceOffer / PriceOfferProduct (GET_PRICE_OFFER -> nexilcolab price offers)
    val poProto = protoOf(PriceOffer::class.js)
    defGet(poProto, "number") { it.unsafeCast<PriceOffer>().number }
    defGet(poProto, "id") { it.unsafeCast<PriceOffer>().id }
    defGet(poProto, "client_id") { it.unsafeCast<PriceOffer>().client_id }
    defGet(poProto, "date") { it.unsafeCast<PriceOffer>().date }
    defGet(poProto, "products") { it.unsafeCast<PriceOffer>().products }
    defGet(poProto, "company_id") { it.unsafeCast<PriceOffer>().company_id }
    defGet(poProto, "name") { it.unsafeCast<PriceOffer>().name }
    defGet(poProto, "created") { it.unsafeCast<PriceOffer>().created }
    // Do not shim products_list — lazy val; JS reads products JSON via price-offers.api.ts instead

    val popProto = protoOf(PriceOfferProduct::class.js)
    defGet(popProto, "id") { it.unsafeCast<PriceOfferProduct>().id }
    defGet(popProto, "v") { it.unsafeCast<PriceOfferProduct>().v }
    defGet(popProto, "price") { it.unsafeCast<PriceOfferProduct>().price }
    defGet(popProto, "text") { it.unsafeCast<PriceOfferProduct>().text }

    defGet(protoOf(Price::class.js), "product") { it.unsafeCast<Price>().product }
}

private fun installProductDeliveryShims() {
    val proto = protoOf(ProductDelivery::class.js)
    defAcc(proto, "productId", { it.unsafeCast<ProductDelivery>().productId }, { s, v -> s.unsafeCast<ProductDelivery>().productId = v.unsafeCast<Int>() })
    defAcc(proto, "value", { it.unsafeCast<ProductDelivery>().value }, { s, v -> s.unsafeCast<ProductDelivery>().value = v.unsafeCast<Float>() })
    defAcc(proto, "returns", { it.unsafeCast<ProductDelivery>().returns }, { s, v -> s.unsafeCast<ProductDelivery>().returns = v.unsafeCast<Float>() })
    defAcc(proto, "price", { it.unsafeCast<ProductDelivery>().price }, { s, v -> s.unsafeCast<ProductDelivery>().price = v.unsafeCast<Float>() })
    defAcc(proto, "supplier_id", { it.unsafeCast<ProductDelivery>().supplier_id }, { s, v -> s.unsafeCast<ProductDelivery>().supplier_id = v.unsafeCast<Int>() })
    defAcc(proto, "use_price", { it.unsafeCast<ProductDelivery>().use_price }, { s, v -> s.unsafeCast<ProductDelivery>().use_price = v.unsafeCast<Byte>() })
    defAcc(proto, "use_price_tax_note", { it.unsafeCast<ProductDelivery>().use_price_tax_note }, { s, v -> s.unsafeCast<ProductDelivery>().use_price_tax_note = v.unsafeCast<Byte>() })
    defAcc(proto, "discount", { it.unsafeCast<ProductDelivery>().discount }, { s, v -> s.unsafeCast<ProductDelivery>().discount = v.unsafeCast<Float>() })
    defAcc(proto, "wrapped_amount", { it.unsafeCast<ProductDelivery>().wrapped_amount }, { s, v -> s.unsafeCast<ProductDelivery>().wrapped_amount = v.unsafeCast<Float>() })
    defAcc(proto, "wrapped_amount_return", { it.unsafeCast<ProductDelivery>().wrapped_amount_return }, { s, v -> s.unsafeCast<ProductDelivery>().wrapped_amount_return = v.unsafeCast<Float>() })
    defAcc(proto, "conversion_ratio", { it.unsafeCast<ProductDelivery>().conversion_ratio }, { s, v -> s.unsafeCast<ProductDelivery>().conversion_ratio = v.unsafeCast<Float>() })
    defAcc(proto, "wrapped_unit", { it.unsafeCast<ProductDelivery>().wrapped_unit }, { s, v -> s.unsafeCast<ProductDelivery>().wrapped_unit = v.unsafeCast<Int>() })
    defAcc(proto, "third_amount", { it.unsafeCast<ProductDelivery>().third_amount }, { s, v -> s.unsafeCast<ProductDelivery>().third_amount = v.unsafeCast<Float>() })
    defAcc(proto, "third_unit", { it.unsafeCast<ProductDelivery>().third_unit }, { s, v -> s.unsafeCast<ProductDelivery>().third_unit = v.unsafeCast<Int>() })
    defAcc(proto, "notes", { it.unsafeCast<ProductDelivery>().notes }, { s, v -> s.unsafeCast<ProductDelivery>().notes = v.unsafeCast<String?>() })
    defAcc(proto, "notes2", { it.unsafeCast<ProductDelivery>().notes2 }, { s, v -> s.unsafeCast<ProductDelivery>().notes2 = v.unsafeCast<String?>() })
    defAcc(proto, "notes3", { it.unsafeCast<ProductDelivery>().notes3 }, { s, v -> s.unsafeCast<ProductDelivery>().notes3 = v.unsafeCast<String?>() })
    defAcc(proto, "doneState", { it.unsafeCast<ProductDelivery>().doneState }, { s, v -> s.unsafeCast<ProductDelivery>().doneState = v.unsafeCast<Byte>() })
    defAcc(proto, "cost", { it.unsafeCast<ProductDelivery>().cost }, { s, v -> s.unsafeCast<ProductDelivery>().cost = v.unsafeCast<Float?>() })
    defAcc(proto, "tempPosition", { it.unsafeCast<ProductDelivery>().tempPosition }, { s, v -> s.unsafeCast<ProductDelivery>().tempPosition = v.unsafeCast<Int>() })
    defAcc(proto, "nmekulkal", { it.unsafeCast<ProductDelivery>().nmekulkal }, { s, v -> s.unsafeCast<ProductDelivery>().nmekulkal = v.unsafeCast<Int>() })
    defAcc(proto, "o_locked", { it.unsafeCast<ProductDelivery>().o_locked }, { s, v -> s.unsafeCast<ProductDelivery>().o_locked = v.unsafeCast<Int>() })
    defAcc(proto, "amountClearedMark", { it.unsafeCast<ProductDelivery>().amountClearedMark }, { s, v -> s.unsafeCast<ProductDelivery>().amountClearedMark = v.unsafeCast<Int>() })
    defAcc(proto, "cp", { it.unsafeCast<ProductDelivery>().cp }, { s, v -> s.unsafeCast<ProductDelivery>().cp = v.unsafeCast<Float?>() })
    defAcc(proto, "cu", { it.unsafeCast<ProductDelivery>().cu }, { s, v -> s.unsafeCast<ProductDelivery>().cu = v.unsafeCast<Int?>() })
    defAcc(proto, "cd", { it.unsafeCast<ProductDelivery>().cd }, { s, v -> s.unsafeCast<ProductDelivery>().cd = v.unsafeCast<Float?>() })
    defAcc(proto, "ci", { it.unsafeCast<ProductDelivery>().ci }, { s, v -> s.unsafeCast<ProductDelivery>().ci = v.unsafeCast<Int?>() })
    defAcc(proto, "isClient", { it.unsafeCast<ProductDelivery>().isClient }, { s, v -> s.unsafeCast<ProductDelivery>().isClient = v.unsafeCast<Boolean>() })
    defAcc(proto, "useWrapped", { it.unsafeCast<ProductDelivery>().useWrapped }, { s, v -> s.unsafeCast<ProductDelivery>().useWrapped = v.unsafeCast<Byte>() })
    defAcc(proto, "changedPrice", { it.unsafeCast<ProductDelivery>().changedPrice }, { s, v -> s.unsafeCast<ProductDelivery>().changedPrice = v.unsafeCast<Byte>() })
    defAcc(proto, "priceBefore", { it.unsafeCast<ProductDelivery>().priceBefore }, { s, v -> s.unsafeCast<ProductDelivery>().priceBefore = v.unsafeCast<Float?>() })
    defAcc(proto, "priceAfter", { it.unsafeCast<ProductDelivery>().priceAfter }, { s, v -> s.unsafeCast<ProductDelivery>().priceAfter = v.unsafeCast<Float?>() })
}

private fun installMiscShims() {
    val hashavshevetProto = protoOf(HashavshevetS::class.js)
    defGet(hashavshevetProto, "last_invoice_id") { it.unsafeCast<HashavshevetS>().last_invoice_id }
    defGet(hashavshevetProto, "last_refund_id") { it.unsafeCast<HashavshevetS>().last_refund_id }
    defGet(hashavshevetProto, "last_invoice_payment_id") { it.unsafeCast<HashavshevetS>().last_invoice_payment_id }
    defGet(hashavshevetProto, "last_receipt_id") { it.unsafeCast<HashavshevetS>().last_receipt_id }
    defGet(hashavshevetProto, "last_deposit_id") { it.unsafeCast<HashavshevetS>().last_deposit_id }

    val cofcProto = protoOf(CofcConf::class.js)
    defGet(cofcProto, "state") { it.unsafeCast<CofcConf>().state }
    defGet(cofcProto, "date_control") { it.unsafeCast<CofcConf>().date_control }
    defGet(cofcProto, "with_price") { it.unsafeCast<CofcConf>().with_price }
    defGet(cofcProto, "conversion_rate_blocker") { it.unsafeCast<CofcConf>().conversion_rate_blocker }
    defGet(cofcProto, "filterOnlyUse") { it.unsafeCast<CofcConf>().filterOnlyUse }
    defGet(cofcProto, "sortByCategory") { it.unsafeCast<CofcConf>().sortByCategory }
    defGet(cofcProto, "without_images") { it.unsafeCast<CofcConf>().without_images }
    defGet(cofcProto, "product_id") { it.unsafeCast<CofcConf>().product_id }
    defAcc(cofcProto, "time_close", { it.unsafeCast<CofcConf>().time_close }, { s, v -> s.unsafeCast<CofcConf>().time_close = v.unsafeCast<String?>() })
    defGet(cofcProto, "daily_link") { it.unsafeCast<CofcConf>().daily_link }
    defGet(cofcProto, "daily_close") { it.unsafeCast<CofcConf>().daily_close }
    defGet(cofcProto, "auto_open_without_cart") { it.unsafeCast<CofcConf>().auto_open_without_cart }
    defGet(cofcProto, "text") { it.unsafeCast<CofcConf>().text }
    defGet(cofcProto, "stock") { it.unsafeCast<CofcConf>().stock }
    defGet(cofcProto, "credit") { it.unsafeCast<CofcConf>().credit }
    defGet(cofcProto, "multi_order") { it.unsafeCast<CofcConf>().multi_order }
    defGet(cofcProto, "no_need") { it.unsafeCast<CofcConf>().no_need }
    defGet(cofcProto, "need_english") { it.unsafeCast<CofcConf>().need_english }
    defGet(cofcProto, "need_notes") { it.unsafeCast<CofcConf>().need_notes }
    defGet(cofcProto, "base_order") { it.unsafeCast<CofcConf>().base_order }
    defAcc(cofcProto, "time1", { it.unsafeCast<CofcConf>().time1 }, { s, v -> s.unsafeCast<CofcConf>().time1 = v.unsafeCast<String?>() })
    defAcc(cofcProto, "time2", { it.unsafeCast<CofcConf>().time2 }, { s, v -> s.unsafeCast<CofcConf>().time2 = v.unsafeCast<String?>() })
    defAcc(cofcProto, "time3", { it.unsafeCast<CofcConf>().time3 }, { s, v -> s.unsafeCast<CofcConf>().time3 = v.unsafeCast<String?>() })
    defAcc(cofcProto, "time4", { it.unsafeCast<CofcConf>().time4 }, { s, v -> s.unsafeCast<CofcConf>().time4 = v.unsafeCast<String?>() })
    defAcc(cofcProto, "time5", { it.unsafeCast<CofcConf>().time5 }, { s, v -> s.unsafeCast<CofcConf>().time5 = v.unsafeCast<String?>() })
    defAcc(cofcProto, "time6", { it.unsafeCast<CofcConf>().time6 }, { s, v -> s.unsafeCast<CofcConf>().time6 = v.unsafeCast<String?>() })
    defAcc(cofcProto, "time7", { it.unsafeCast<CofcConf>().time7 }, { s, v -> s.unsafeCast<CofcConf>().time7 = v.unsafeCast<String?>() })
    defGet(cofcProto, "delivery") { it.unsafeCast<CofcConf>().delivery }
    defGet(cofcProto, "delivery_free_th") { it.unsafeCast<CofcConf>().delivery_free_th }
    defGet(cofcProto, "return_state") { it.unsafeCast<CofcConf>().return_state }
    defGet(cofcProto, "return_pct_mode") { it.unsafeCast<CofcConf>().return_pct_mode }
    defGet(cofcProto, "unlock_p") { it.unsafeCast<CofcConf>().unlock_p }
    defGet(cofcProto, "full_prod") { it.unsafeCast<CofcConf>().full_prod }
    defGet(cofcProto, "inverse_avail") { it.unsafeCast<CofcConf>().inverse_avail }
    defGet(cofcProto, "match_dv") { it.unsafeCast<CofcConf>().match_dv }
    defGet(cofcProto, "rep_link") { it.unsafeCast<CofcConf>().rep_link }
    defGet(cofcProto, "blocked_dates") { it.unsafeCast<CofcConf>().blocked_dates }
    defGet(cofcProto, "third_unit_on") { it.unsafeCast<CofcConf>().third_unit_on }
    defGet(cofcProto, "agent_constraints") { it.unsafeCast<CofcConf>().agent_constraints }
    defGet(cofcProto, "weekend_block") { it.unsafeCast<CofcConf>().weekend_block }
    defGet(cofcProto, "link_version") { it.unsafeCast<CofcConf>().link_version }
    defGet(cofcProto, "btc_client_id") { it.unsafeCast<CofcConf>().btc_client_id }
    defGet(cofcProto, "statistics") { it.unsafeCast<CofcConf>().statistics }
    defGet(cofcProto, "personal_area") { it.unsafeCast<CofcConf>().personal_area }
    defGet(cofcProto, "required_fields") { it.unsafeCast<CofcConf>().required_fields }
    defGet(cofcProto, "block_rules") { it.unsafeCast<CofcConf>().block_rules }
    defGet(cofcProto, "popup_message") { it.unsafeCast<CofcConf>().popup_message }
    defGet(cofcProto, "day_time_locks") { it.unsafeCast<CofcConf>().day_time_locks }
    defGet(cofcProto, "admin_flags") { it.unsafeCast<CofcConf>().admin_flags }
    defGet(cofcProto, "clientsign_docs") { it.unsafeCast<CofcConf>().clientsign_docs }
    defGet(cofcProto, "clientsign_terms") { it.unsafeCast<CofcConf>().clientsign_terms }
    defGet(cofcProto, "product_date_windows") { it.unsafeCast<CofcConf>().product_date_windows }
    defGet(cofcProto, "display_settings") { it.unsafeCast<CofcConf>().display_settings }
    defGet(cofcProto, "allow_locked_existing_removal") { it.unsafeCast<CofcConf>().allow_locked_existing_removal }
    defGet(cofcProto, "delivery_cutoff_rules") { it.unsafeCast<CofcConf>().delivery_cutoff_rules }
    defGet(cofcProto, "hide_unavailable") { it.unsafeCast<CofcConf>().hide_unavailable }

    val apiProto = protoOf(ApiConfig::class.js)
    defGet(apiProto, "storeNextNote") { it.unsafeCast<ApiConfig>().storeNextNote }
    defGet(apiProto, "storeNextTax") { it.unsafeCast<ApiConfig>().storeNextTax }
    defGet(apiProto, "managerOrder") { it.unsafeCast<ApiConfig>().managerOrder }
    defGet(apiProto, "managerNote") { it.unsafeCast<ApiConfig>().managerNote }
    defGet(apiProto, "managerTax") { it.unsafeCast<ApiConfig>().managerTax }
    defGet(apiProto, "oneNote") { it.unsafeCast<ApiConfig>().oneNote }
    defGet(apiProto, "oneOrder") { it.unsafeCast<ApiConfig>().oneOrder }
    defGet(apiProto, "oneTax") { it.unsafeCast<ApiConfig>().oneTax }
    defGet(apiProto, "hashavshevetNote") { it.unsafeCast<ApiConfig>().hashavshevetNote }
    defGet(apiProto, "hashavshevetTax") { it.unsafeCast<ApiConfig>().hashavshevetTax }
    defGet(apiProto, "read_products") { it.unsafeCast<ApiConfig>().read_products }
    defGet(apiProto, "read_customers") { it.unsafeCast<ApiConfig>().read_customers }
    defGet(apiProto, "read_prices") { it.unsafeCast<ApiConfig>().read_prices }
    defGet(apiProto, "mailOrder") { it.unsafeCast<ApiConfig>().mailOrder }
    defGet(apiProto, "mailTaxNote") { it.unsafeCast<ApiConfig>().mailTaxNote }
    defGet(apiProto, "mailNote") { it.unsafeCast<ApiConfig>().mailNote }
    defGet(apiProto, "read_orders") { it.unsafeCast<ApiConfig>().read_orders }
    defGet(apiProto, "whatsapp") { it.unsafeCast<ApiConfig>().whatsapp }
    defGet(apiProto, "ai") { it.unsafeCast<ApiConfig>().ai }
    defGet(apiProto, "email") { it.unsafeCast<ApiConfig>().email }
    defGet(apiProto, "last_counter") { it.unsafeCast<ApiConfig>().last_counter }
    defGet(apiProto, "credit") { it.unsafeCast<ApiConfig>().credit }
    defMethod0(apiProto, "hasExternalSoftware") { it.unsafeCast<ApiConfig>().hasExternalSoftware() }

    val osProto = protoOf(OrderStatus::class.js)
    defGet(osProto, "state") { it.unsafeCast<OrderStatus>().state }

    val adhProto = protoOf(AmountsDataHolder::class.js)
    defAcc(adhProto, "value", { it.unsafeCast<AmountsDataHolder>().value }, { s, v -> s.unsafeCast<AmountsDataHolder>().value = v.unsafeCast<Float>() })
    defAcc(adhProto, "returns", { it.unsafeCast<AmountsDataHolder>().returns }, { s, v -> s.unsafeCast<AmountsDataHolder>().returns = v.unsafeCast<Float>() })
    defAcc(adhProto, "taxPaid", { it.unsafeCast<AmountsDataHolder>().taxPaid }, { s, v -> s.unsafeCast<AmountsDataHolder>().taxPaid = v.unsafeCast<Float>() })
    defAcc(adhProto, "money", { it.unsafeCast<AmountsDataHolder>().money }, { s, v -> s.unsafeCast<AmountsDataHolder>().money = v.unsafeCast<Float>() })
    defAcc(adhProto, "wrappedMoney", { it.unsafeCast<AmountsDataHolder>().wrappedMoney }, { s, v -> s.unsafeCast<AmountsDataHolder>().wrappedMoney = v.unsafeCast<Float>() })
    defAcc(adhProto, "wrappedTaxPaid", { it.unsafeCast<AmountsDataHolder>().wrappedTaxPaid }, { s, v -> s.unsafeCast<AmountsDataHolder>().wrappedTaxPaid = v.unsafeCast<Float>() })
    defAcc(adhProto, "wrappedValue", { it.unsafeCast<AmountsDataHolder>().wrappedValue }, { s, v -> s.unsafeCast<AmountsDataHolder>().wrappedValue = v.unsafeCast<Float>() })
    defAcc(adhProto, "wrappedReturn", { it.unsafeCast<AmountsDataHolder>().wrappedReturn }, { s, v -> s.unsafeCast<AmountsDataHolder>().wrappedReturn = v.unsafeCast<Float>() })

    val locProto = protoOf(Location::class.js)
    defGet(locProto, "lon") { it.unsafeCast<Location>().lon }
    defGet(locProto, "lat") { it.unsafeCast<Location>().lat }
    defGet(locProto, "city") { it.unsafeCast<Location>().city }
    defGet(locProto, "street") { it.unsafeCast<Location>().street }
    defGet(locProto, "streetNumber") { it.unsafeCast<Location>().streetNumber }

    val csdProto = protoOf(ClientStaticData::class.js)
    defGet(csdProto, "id") { it.unsafeCast<ClientStaticData>().id }
    defAcc(csdProto, "business_name", { it.unsafeCast<ClientStaticData>().business_name }, { s, v -> s.unsafeCast<ClientStaticData>().business_name = v.unsafeCast<String>() })
    defGet(csdProto, "business_id") { it.unsafeCast<ClientStaticData>().business_id }
    defAcc(csdProto, "address", { it.unsafeCast<ClientStaticData>().address }, { s, v -> s.unsafeCast<ClientStaticData>().address = v.unsafeCast<String>() })
    defGet(csdProto, "phone") { it.unsafeCast<ClientStaticData>().phone }
    defAcc(csdProto, "supplier_input_equipment", { it.unsafeCast<ClientStaticData>().supplier_input_equipment }, { s, v -> s.unsafeCast<ClientStaticData>().supplier_input_equipment = v.unsafeCast<Boolean>() })
    defGet(csdProto, "city") { it.unsafeCast<ClientStaticData>().city }
    defGet(csdProto, "street") { it.unsafeCast<ClientStaticData>().street }
    defGet(csdProto, "streetNumber") { it.unsafeCast<ClientStaticData>().streetNumber }
    defMethod0(csdProto, "getAddressStr") { it.unsafeCast<ClientStaticData>().getAddressStr() }

    val ptdProto = protoOf(PaymentData::class.js)
    defGet(ptdProto, "value") { it.unsafeCast<PaymentData>().value }
    defGet(ptdProto, "notes") { it.unsafeCast<PaymentData>().notes }
    defAcc(ptdProto, "agent", { it.unsafeCast<PaymentData>().agent }, { s, v -> s.unsafeCast<PaymentData>().agent = v.unsafeCast<String?>() })
    defAcc(ptdProto, "payMethod", { it.unsafeCast<PaymentData>().payMethod }, { s, v -> s.unsafeCast<PaymentData>().payMethod = v.unsafeCast<Utils.PayMethod>() })
    defGet(ptdProto, "pay_method") { it.unsafeCast<PaymentData>().pay_method }
    defGet(ptdProto, "bank_id") { it.unsafeCast<PaymentData>().bank_id }
    defGet(ptdProto, "bank_branch") { it.unsafeCast<PaymentData>().bank_branch }
    defGet(ptdProto, "bank_number") { it.unsafeCast<PaymentData>().bank_number }
    defGet(ptdProto, "check_number") { it.unsafeCast<PaymentData>().check_number }
    defGet(ptdProto, "check_date") { it.unsafeCast<PaymentData>().check_date }
    defGet(ptdProto, "paid") { it.unsafeCast<PaymentData>().paid }
    defAcc(ptdProto, "paid_date", { it.unsafeCast<PaymentData>().paid_date }, { s, v -> s.unsafeCast<PaymentData>().paid_date = v.unsafeCast<String?>() })
    defGet(ptdProto, "paid_details") { it.unsafeCast<PaymentData>().paid_details }
    defGet(ptdProto, "category") { it.unsafeCast<PaymentData>().category }
    defGet(ptdProto, "canceled") { it.unsafeCast<PaymentData>().canceled }
    defGet(ptdProto, "inner_id") { it.unsafeCast<PaymentData>().inner_id }
    defAcc(ptdProto, "id", { it.unsafeCast<PaymentData>().id }, { s, v -> s.unsafeCast<PaymentData>().id = v.unsafeCast<Int?>() })
    defAcc(ptdProto, "client_id", { it.unsafeCast<PaymentData>().client_id }, { s, v -> s.unsafeCast<PaymentData>().client_id = v.unsafeCast<Int?>() })
    defAcc(ptdProto, "document_date", { it.unsafeCast<PaymentData>().document_date }, { s, v -> s.unsafeCast<PaymentData>().document_date = v.unsafeCast<String?>() })
    defAcc(ptdProto, "date", { it.unsafeCast<PaymentData>().date }, { s, v -> s.unsafeCast<PaymentData>().date = v.unsafeCast<String?>() })

    val ctndProto = protoOf(ClientTaxNoteData::class.js)
    defGet(ctndProto, "value") { it.unsafeCast<ClientTaxNoteData>().value }
    defAcc(ctndProto, "taxPaid", { it.unsafeCast<ClientTaxNoteData>().taxPaid }, { s, v -> s.unsafeCast<ClientTaxNoteData>().taxPaid = v.unsafeCast<Float?>() })
    defGet(ctndProto, "amount") { it.unsafeCast<ClientTaxNoteData>().amount }
    defGet(ctndProto, "fill_type") { it.unsafeCast<ClientTaxNoteData>().fill_type }
    defAcc(ctndProto, "details", { it.unsafeCast<ClientTaxNoteData>().details }, { s, v -> s.unsafeCast<ClientTaxNoteData>().details = v.unsafeCast<String>() })

    val cpdProto = protoOf(ClientProductData::class.js)
    defGet(cpdProto, "client_id") { it.unsafeCast<ClientProductData>().client_id }
    defGet(cpdProto, "product_id") { it.unsafeCast<ClientProductData>().product_id }
    defGet(cpdProto, "barcode") { it.unsafeCast<ClientProductData>().barcode }
    defGet(cpdProto, "returns") { it.unsafeCast<ClientProductData>().returns }

    // Client daily data (driver/collector assignment per client per day). The
    // orders datasheet reads these to attach drivers/collectors to orders; with
    // no shim every field was mangled to undefined -> no attachments app-wide.
    val cddProto = protoOf(ClientDailyData::class.js)
    defGet(cddProto, "date") { it.unsafeCast<ClientDailyData>().date }
    defGet(cddProto, "id") { it.unsafeCast<ClientDailyData>().id }
    defGet(cddProto, "driver_id") { it.unsafeCast<ClientDailyData>().driver_id }
    defGet(cddProto, "collector_id") { it.unsafeCast<ClientDailyData>().collector_id }
    defGet(cddProto, "position") { it.unsafeCast<ClientDailyData>().position }
    defGet(cddProto, "position_col") { it.unsafeCast<ClientDailyData>().position_col }
    defGet(cddProto, "group_id") { it.unsafeCast<ClientDailyData>().group_id }
    defGet(cddProto, "car") { it.unsafeCast<ClientDailyData>().car }
    defGet(cddProto, "active") { it.unsafeCast<ClientDailyData>().active }

    // DataQueryHolder is the result row of every analytics query (dashboard,
    // anomalies, spreads). Without a shim, r.byEnt/r.valueSum/etc. were all
    // mangled to undefined -> getClient(undefined).first == null -> getCity()
    // crash across all analytics screens.
    val dqhProto = protoOf(DataQueryHolder::class.js)
    defGet(dqhProto, "doc_type") { it.unsafeCast<DataQueryHolder>().doc_type }
    defGet(dqhProto, "valueSum") { it.unsafeCast<DataQueryHolder>().valueSum }
    defGet(dqhProto, "returnSum") { it.unsafeCast<DataQueryHolder>().returnSum }
    defGet(dqhProto, "number_of_rows") { it.unsafeCast<DataQueryHolder>().number_of_rows }
    defGet(dqhProto, "byMoney") { it.unsafeCast<DataQueryHolder>().byMoney }
    defGet(dqhProto, "byTax") { it.unsafeCast<DataQueryHolder>().byTax }
    defGet(dqhProto, "valueSumSec") { it.unsafeCast<DataQueryHolder>().valueSumSec }
    defGet(dqhProto, "returnSumSec") { it.unsafeCast<DataQueryHolder>().returnSumSec }
    defGet(dqhProto, "valueSumThird") { it.unsafeCast<DataQueryHolder>().valueSumThird }
    defGet(dqhProto, "valueSumSecJoin") { it.unsafeCast<DataQueryHolder>().valueSumSecJoin }
    defGet(dqhProto, "returnSumSecJoin") { it.unsafeCast<DataQueryHolder>().returnSumSecJoin }
    defGet(dqhProto, "byMoneyJoin") { it.unsafeCast<DataQueryHolder>().byMoneyJoin }
    defGet(dqhProto, "byTaxJoin") { it.unsafeCast<DataQueryHolder>().byTaxJoin }
    defGet(dqhProto, "byYear") { it.unsafeCast<DataQueryHolder>().byYear }
    defGet(dqhProto, "byMonth") { it.unsafeCast<DataQueryHolder>().byMonth }
    defGet(dqhProto, "byWeek") { it.unsafeCast<DataQueryHolder>().byWeek }
    defGet(dqhProto, "byDay") { it.unsafeCast<DataQueryHolder>().byDay }
    defGet(dqhProto, "byEnt") { it.unsafeCast<DataQueryHolder>().byEnt }
    defGet(dqhProto, "byProduct") { it.unsafeCast<DataQueryHolder>().byProduct }
    defGet(dqhProto, "byAgent") { it.unsafeCast<DataQueryHolder>().byAgent }
    defGet(dqhProto, "stdValue") { it.unsafeCast<DataQueryHolder>().stdValue }
    defGet(dqhProto, "stdReturn") { it.unsafeCast<DataQueryHolder>().stdReturn }
    defGet(dqhProto, "counter") { it.unsafeCast<DataQueryHolder>().counter }
    defGet(dqhProto, "date") { it.unsafeCast<DataQueryHolder>().date }
    defGet(dqhProto, "byCost") { it.unsafeCast<DataQueryHolder>().byCost }
    defGet(dqhProto, "byCostRet") { it.unsafeCast<DataQueryHolder>().byCostRet }
    defGet(dqhProto, "byCostJoin") { it.unsafeCast<DataQueryHolder>().byCostJoin }
    defGet(dqhProto, "byCostJoinRet") { it.unsafeCast<DataQueryHolder>().byCostJoinRet }
    defGet(dqhProto, "byAgentId") { it.unsafeCast<DataQueryHolder>().byAgentId }
    defGet(dqhProto, "id") { it.unsafeCast<DataQueryHolder>().id }
    defGet(dqhProto, "oClosed") { it.unsafeCast<DataQueryHolder>().oClosed }
    defGet(dqhProto, "wvca") { it.unsafeCast<DataQueryHolder>().wvca }
    defGet(dqhProto, "multiplyValue") { it.unsafeCast<DataQueryHolder>().multiplyValue }

    // DailySummery is returned by GET_DAILY_SUMMARY for the dashboard's daily
    // panel (קופה / payment breakdown). Unshimmed -> every field undefined -> 0.
    val dsProto = protoOf(DailySummery::class.js)
    defGet(dsProto, "sales") { it.unsafeCast<DailySummery>().sales }
    defGet(dsProto, "items") { it.unsafeCast<DailySummery>().items }
    defGet(dsProto, "invoices") { it.unsafeCast<DailySummery>().invoices }
    defGet(dsProto, "invoicesRegular") { it.unsafeCast<DailySummery>().invoicesRegular }
    defGet(dsProto, "invoicesWithPayment") { it.unsafeCast<DailySummery>().invoicesWithPayment }
    defGet(dsProto, "creditNotes") { it.unsafeCast<DailySummery>().creditNotes }
    defGet(dsProto, "receiptsSum") { it.unsafeCast<DailySummery>().receiptsSum }
    defGet(dsProto, "receiptsCount") { it.unsafeCast<DailySummery>().receiptsCount }
    defGet(dsProto, "visits") { it.unsafeCast<DailySummery>().visits }
    defGet(dsProto, "cash") { it.unsafeCast<DailySummery>().cash }
    defGet(dsProto, "checks") { it.unsafeCast<DailySummery>().checks }
    defGet(dsProto, "credit") { it.unsafeCast<DailySummery>().credit }
    defGet(dsProto, "transfer") { it.unsafeCast<DailySummery>().transfer }
    defGet(dsProto, "taxDeduction") { it.unsafeCast<DailySummery>().taxDeduction }
    defGet(dsProto, "payments") { it.unsafeCast<DailySummery>().payments }
    defGet(dsProto, "taxes") { it.unsafeCast<DailySummery>().taxes }

    // Client visitation (analytics visitation report / all-data). @myName on the
    // class names it but NOT its fields, so they were mangled. Holder has none.
    val cvProto = protoOf(ClientVisitation::class.js)
    defGet(cvProto, "client_id") { it.unsafeCast<ClientVisitation>().client_id }
    defGet(cvProto, "start_week") { it.unsafeCast<ClientVisitation>().start_week }
    defGet(cvProto, "end_week") { it.unsafeCast<ClientVisitation>().end_week }
    defGet(cvProto, "valueSum") { it.unsafeCast<ClientVisitation>().valueSum }
    defGet(cvProto, "returnSum") { it.unsafeCast<ClientVisitation>().returnSum }
    defGet(cvProto, "valueSumSec") { it.unsafeCast<ClientVisitation>().valueSumSec }
    defGet(cvProto, "returnSumSec") { it.unsafeCast<ClientVisitation>().returnSumSec }
    defGet(cvProto, "dv_visiting") { it.unsafeCast<ClientVisitation>().dv_visiting }
    defGet(cvProto, "dv_documents") { it.unsafeCast<ClientVisitation>().dv_documents }
    defGet(cvProto, "tn_visiting") { it.unsafeCast<ClientVisitation>().tn_visiting }
    defGet(cvProto, "tn_documents") { it.unsafeCast<ClientVisitation>().tn_documents }
    defGet(cvProto, "pay_visiting") { it.unsafeCast<ClientVisitation>().pay_visiting }
    defGet(cvProto, "pay_documents") { it.unsafeCast<ClientVisitation>().pay_documents }
    defGet(cvProto, "visit_visiting") { it.unsafeCast<ClientVisitation>().visit_visiting }
    defGet(cvProto, "visit_documents") { it.unsafeCast<ClientVisitation>().visit_documents }
    defGet(cvProto, "total_visit") { it.unsafeCast<ClientVisitation>().total_visit }
    defGet(cvProto, "comments") { it.unsafeCast<ClientVisitation>().comments }

    val cvhProto = protoOf(ClientVisitationHolder::class.js)
    defGet(cvhProto, "client_id") { it.unsafeCast<ClientVisitationHolder>().client_id }
    defGet(cvhProto, "data") { it.unsafeCast<ClientVisitationHolder>().data }
    defGet(cvhProto, "from") { it.unsafeCast<ClientVisitationHolder>().from }
    defGet(cvhProto, "to") { it.unsafeCast<ClientVisitationHolder>().to }
    defGet(cvhProto, "placeHolder") { it.unsafeCast<ClientVisitationHolder>().placeHolder }
    defGet(cvhProto, "dv_visiting") { it.unsafeCast<ClientVisitationHolder>().dv_visiting }
    defGet(cvhProto, "dv_documents") { it.unsafeCast<ClientVisitationHolder>().dv_documents }
    defGet(cvhProto, "tn_visiting") { it.unsafeCast<ClientVisitationHolder>().tn_visiting }
    defGet(cvhProto, "tn_documents") { it.unsafeCast<ClientVisitationHolder>().tn_documents }
    defGet(cvhProto, "pay_visiting") { it.unsafeCast<ClientVisitationHolder>().pay_visiting }
    defGet(cvhProto, "pay_documents") { it.unsafeCast<ClientVisitationHolder>().pay_documents }
    defGet(cvhProto, "visit_visiting") { it.unsafeCast<ClientVisitationHolder>().visit_visiting }
    defGet(cvhProto, "visit_documents") { it.unsafeCast<ClientVisitationHolder>().visit_documents }
    defGet(cvhProto, "valueSum") { it.unsafeCast<ClientVisitationHolder>().valueSum }
    defGet(cvhProto, "returnSum") { it.unsafeCast<ClientVisitationHolder>().returnSum }
    defGet(cvhProto, "valueSumSec") { it.unsafeCast<ClientVisitationHolder>().valueSumSec }
    defGet(cvhProto, "returnSumSec") { it.unsafeCast<ClientVisitationHolder>().returnSumSec }
    defGet(cvhProto, "total_visit") { it.unsafeCast<ClientVisitationHolder>().total_visit }
    defGet(cvhProto, "comments") { it.unsafeCast<ClientVisitationHolder>().comments }

    // getJsonDesc(...) returns this; web reads .name (column/tab labels), and
    // .param/.type/.func/.forceInNote in datasheet/tax-send field builders.
    val rucProto = protoOf(ReadUserCommentJson::class.js)
    defGet(rucProto, "type") { it.unsafeCast<ReadUserCommentJson>().type }
    defGet(rucProto, "param") { it.unsafeCast<ReadUserCommentJson>().param }
    defGet(rucProto, "name") { it.unsafeCast<ReadUserCommentJson>().name }
    defGet(rucProto, "forceInNote") { it.unsafeCast<ReadUserCommentJson>().forceInNote }
    defGet(rucProto, "func") { it.unsafeCast<ReadUserCommentJson>().func }

    // ----- Definitive sweep: remaining report/holder data classes whose fields
    // the web reads but were mangled (class-level @myName doesn't expose fields).

    // ClientDebt (GET_CLIENT_DEBT -> debt reports / all-debts)
    val cdebtProto = protoOf(ClientDebt::class.js)
    defGet(cdebtProto, "client_id") { it.unsafeCast<ClientDebt>().client_id }
    defGet(cdebtProto, "branch") { it.unsafeCast<ClientDebt>().branch }
    defGet(cdebtProto, "notes") { it.unsafeCast<ClientDebt>().notes }
    defGet(cdebtProto, "positive_carteset") { it.unsafeCast<ClientDebt>().positive_carteset }
    defGet(cdebtProto, "negative_carteset") { it.unsafeCast<ClientDebt>().negative_carteset }
    defGet(cdebtProto, "notes_amount") { it.unsafeCast<ClientDebt>().notes_amount }
    defGet(cdebtProto, "notes_open") { it.unsafeCast<ClientDebt>().notes_open }
    defGet(cdebtProto, "taxNotes") { it.unsafeCast<ClientDebt>().taxNotes }
    defGet(cdebtProto, "taxNotes_open") { it.unsafeCast<ClientDebt>().taxNotes_open }
    defGet(cdebtProto, "tax_note_pays") { it.unsafeCast<ClientDebt>().tax_note_pays }
    defGet(cdebtProto, "taxNotes_amount") { it.unsafeCast<ClientDebt>().taxNotes_amount }
    defGet(cdebtProto, "pay_notes") { it.unsafeCast<ClientDebt>().pay_notes }
    defGet(cdebtProto, "pay_notes_amount") { it.unsafeCast<ClientDebt>().pay_notes_amount }
    defGet(cdebtProto, "openSumDue") { it.unsafeCast<ClientDebt>().openSumDue }
    defGet(cdebtProto, "overdue_tax_notes") { it.unsafeCast<ClientDebt>().overdue_tax_notes }
    defGet(cdebtProto, "value_left") { it.unsafeCast<ClientDebt>().value_left }
    defGet(cdebtProto, "mdate_carteset") { it.unsafeCast<ClientDebt>().mdate_carteset }
    defGet(cdebtProto, "openSumDueMonth") { it.unsafeCast<ClientDebt>().openSumDueMonth }
    defGet(cdebtProto, "overdueMonth") { it.unsafeCast<ClientDebt>().overdueMonth }
    defGet(cdebtProto, "earliest_open_date") { it.unsafeCast<ClientDebt>().earliest_open_date }
    defGet(cdebtProto, "ind_upto_2weeks") { it.unsafeCast<ClientDebt>().ind_upto_2weeks }
    defGet(cdebtProto, "ind_upto_1month") { it.unsafeCast<ClientDebt>().ind_upto_1month }
    defGet(cdebtProto, "ind_from_1_5months") { it.unsafeCast<ClientDebt>().ind_from_1_5months }

    // GlulStruct (GET_CLIENT_GIUL_FAST -> aging report)
    val glulProto = protoOf(GlulStruct::class.js)
    defGet(glulProto, "client_id") { it.unsafeCast<GlulStruct>().client_id }
    defGet(glulProto, "branch") { it.unsafeCast<GlulStruct>().branch }
    defGet(glulProto, "totalValueSum") { it.unsafeCast<GlulStruct>().totalValueSum }
    defGet(glulProto, "numOfOpen") { it.unsafeCast<GlulStruct>().numOfOpen }
    defGet(glulProto, "valueSum1") { it.unsafeCast<GlulStruct>().valueSum1 }
    defGet(glulProto, "openAmount1") { it.unsafeCast<GlulStruct>().openAmount1 }
    defGet(glulProto, "valueSum2") { it.unsafeCast<GlulStruct>().valueSum2 }
    defGet(glulProto, "openAmount2") { it.unsafeCast<GlulStruct>().openAmount2 }
    defGet(glulProto, "valueSum3") { it.unsafeCast<GlulStruct>().valueSum3 }
    defGet(glulProto, "openAmount3") { it.unsafeCast<GlulStruct>().openAmount3 }
    defGet(glulProto, "valueSum4") { it.unsafeCast<GlulStruct>().valueSum4 }
    defGet(glulProto, "openAmount4") { it.unsafeCast<GlulStruct>().openAmount4 }
    defGet(glulProto, "restTotalValueSum") { it.unsafeCast<GlulStruct>().restTotalValueSum }
    defGet(glulProto, "restNumOpen") { it.unsafeCast<GlulStruct>().restNumOpen }

    // InventoryItem (GET_INVENTORY / GET_INVENTORY_NOTE)
    val invProto = protoOf(InventoryItem::class.js)
    defGet(invProto, "agent_id") { it.unsafeCast<InventoryItem>().agent_id }
    defGet(invProto, "product_id") { it.unsafeCast<InventoryItem>().product_id }
    defGet(invProto, "valueSum") { it.unsafeCast<InventoryItem>().valueSum }
    defGet(invProto, "returnSum") { it.unsafeCast<InventoryItem>().returnSum }
    defGet(invProto, "valueSumSec") { it.unsafeCast<InventoryItem>().valueSumSec }
    defGet(invProto, "returnSumSec") { it.unsafeCast<InventoryItem>().returnSumSec }
    defGet(invProto, "updated") { it.unsafeCast<InventoryItem>().updated }
    defGet(invProto, "inventoryStart") { it.unsafeCast<InventoryItem>().inventoryStart }
    defGet(invProto, "inventoryStart2") { it.unsafeCast<InventoryItem>().inventoryStart2 }
    defGet(invProto, "amountDistribution") { it.unsafeCast<InventoryItem>().amountDistribution }
    defGet(invProto, "amountDistribution2") { it.unsafeCast<InventoryItem>().amountDistribution2 }
    defGet(invProto, "type") { it.unsafeCast<InventoryItem>().type }
    defGet(invProto, "inv_type") { it.unsafeCast<InventoryItem>().inv_type }
    defGet(invProto, "date") { it.unsafeCast<InventoryItem>().date }
    defGet(invProto, "inventoryMatch") { it.unsafeCast<InventoryItem>().inventoryMatch }
    defGet(invProto, "inventoryMatchSec") { it.unsafeCast<InventoryItem>().inventoryMatchSec }
    defGet(invProto, "lastInventory") { it.unsafeCast<InventoryItem>().lastInventory }
    defGet(invProto, "lastInventorySec") { it.unsafeCast<InventoryItem>().lastInventorySec }

    // Available (product availability; nexilcolab + order-frontend)
    val availProto = protoOf(Available::class.js)
    defGet(availProto, "id") { it.unsafeCast<Available>().id }
    defGet(availProto, "product_id") { it.unsafeCast<Available>().product_id }
    defGet(availProto, "available") { it.unsafeCast<Available>().available }
    defGet(availProto, "priority") { it.unsafeCast<Available>().priority }
    defGet(availProto, "inventory") { it.unsafeCast<Available>().inventory }
    defGet(availProto, "master") { it.unsafeCast<Available>().master }
    defGet(availProto, "inverse") { it.unsafeCast<Available>().inverse }

    // MsgTracker (message tracker drawer)
    val mtProto = protoOf(MsgTracker::class.js)
    defGet(mtProto, "unique_id") { it.unsafeCast<MsgTracker>().unique_id }
    defGet(mtProto, "contact") { it.unsafeCast<MsgTracker>().contact }
    defGet(mtProto, "method") { it.unsafeCast<MsgTracker>().method }
    defGet(mtProto, "msg_id") { it.unsafeCast<MsgTracker>().msg_id }
    defGet(mtProto, "company_id") { it.unsafeCast<MsgTracker>().company_id }
    defGet(mtProto, "type") { it.unsafeCast<MsgTracker>().type }
    defGet(mtProto, "title") { it.unsafeCast<MsgTracker>().title }
    defGet(mtProto, "msg") { it.unsafeCast<MsgTracker>().msg }
    defGet(mtProto, "send") { it.unsafeCast<MsgTracker>().send }
    defGet(mtProto, "err") { it.unsafeCast<MsgTracker>().err }
    defGet(mtProto, "tries") { it.unsafeCast<MsgTracker>().tries }
    defGet(mtProto, "validate") { it.unsafeCast<MsgTracker>().validate }
    defGet(mtProto, "date_send") { it.unsafeCast<MsgTracker>().date_send }
    defGet(mtProto, "date_updated") { it.unsafeCast<MsgTracker>().date_updated }
    defGet(mtProto, "date_created") { it.unsafeCast<MsgTracker>().date_created }
    defGet(mtProto, "name") { it.unsafeCast<MsgTracker>().name }
    defGet(mtProto, "date_action") { it.unsafeCast<MsgTracker>().date_action }

    // ClientCompare + holder (GET_CLIENT_COMPARE)
    val ccProto = protoOf(ClientCompare::class.js)
    defGet(ccProto, "client_id") { it.unsafeCast<ClientCompare>().client_id }
    defGet(ccProto, "month") { it.unsafeCast<ClientCompare>().month }
    defGet(ccProto, "year") { it.unsafeCast<ClientCompare>().year }
    defGet(ccProto, "week") { it.unsafeCast<ClientCompare>().week }
    defGet(ccProto, "valueSum") { it.unsafeCast<ClientCompare>().valueSum }
    defGet(ccProto, "returnSum") { it.unsafeCast<ClientCompare>().returnSum }
    defGet(ccProto, "valueSumSec") { it.unsafeCast<ClientCompare>().valueSumSec }
    defGet(ccProto, "returnSumSec") { it.unsafeCast<ClientCompare>().returnSumSec }
    defGet(ccProto, "totalMoney") { it.unsafeCast<ClientCompare>().totalMoney }
    defGet(ccProto, "cost") { it.unsafeCast<ClientCompare>().cost }
    val cchProto = protoOf(ClientCompareHolder::class.js)
    defGet(cchProto, "client_id") { it.unsafeCast<ClientCompareHolder>().client_id }
    defGet(cchProto, "data") { it.unsafeCast<ClientCompareHolder>().data }
    defGet(cchProto, "method") { it.unsafeCast<ClientCompareHolder>().method }
    defGet(cchProto, "to") { it.unsafeCast<ClientCompareHolder>().to }

    // CollectionObject / CollectionHolder (order datasheet karton columns)
    val coProto = protoOf(CollectionObject::class.js)
    defGet(coProto, "id") { it.unsafeCast<CollectionObject>().id }
    defGet(coProto, "box") { it.unsafeCast<CollectionObject>().box }
    val chProto = protoOf(CollectionHolder::class.js)
    defGet(chProto, "id") { it.unsafeCast<CollectionHolder>().id }
    defGet(chProto, "box") { it.unsafeCast<CollectionHolder>().box }

    // EdiMember (customer list EDI)
    val ediProto = protoOf(EdiMember::class.js)
    defGet(ediProto, "date") { it.unsafeCast<EdiMember>().date }
    defGet(ediProto, "client_id") { it.unsafeCast<EdiMember>().client_id }
    defGet(ediProto, "edi_sup_id") { it.unsafeCast<EdiMember>().edi_sup_id }
    defGet(ediProto, "edi_client_id") { it.unsafeCast<EdiMember>().edi_client_id }
    defGet(ediProto, "net_edi") { it.unsafeCast<EdiMember>().net_edi }
    defGet(ediProto, "internal_sup_edi") { it.unsafeCast<EdiMember>().internal_sup_edi }
    defGet(ediProto, "branch_number") { it.unsafeCast<EdiMember>().branch_number }

    // OrderStatistics.id (methods getDaily/etc are @myName'd already)
    defGet(protoOf(OrderStatistics::class.js), "id") { it.unsafeCast<OrderStatistics>().id }

    // ProductStatistics (order statistics / anomalies)
    val psProto = protoOf(ProductStatistics::class.js)
    defGet(psProto, "product_id") { it.unsafeCast<ProductStatistics>().product_id }
    defGet(psProto, "amount") { it.unsafeCast<ProductStatistics>().amount }
    defGet(psProto, "returns") { it.unsafeCast<ProductStatistics>().returns }
    defGet(psProto, "amount_second") { it.unsafeCast<ProductStatistics>().amount_second }
    defGet(psProto, "returns_second") { it.unsafeCast<ProductStatistics>().returns_second }
    defGet(psProto, "instances") { it.unsafeCast<ProductStatistics>().instances }
    defGet(psProto, "avg") { it.unsafeCast<ProductStatistics>().avg }
    defGet(psProto, "std") { it.unsafeCast<ProductStatistics>().std }
    defGet(psProto, "stdReturns") { it.unsafeCast<ProductStatistics>().stdReturns }
    defGet(psProto, "counter") { it.unsafeCast<ProductStatistics>().counter }

    // ProductDeliveryFlat (statistics tables); methods getProduct/etc @myName'd
    val pdfProto = protoOf(ProductDeliveryFlat::class.js)
    defGet(pdfProto, "date") { it.unsafeCast<ProductDeliveryFlat>().date }
    defGet(pdfProto, "value") { it.unsafeCast<ProductDeliveryFlat>().value }
    defGet(pdfProto, "returns") { it.unsafeCast<ProductDeliveryFlat>().returns }
    defGet(pdfProto, "wrapped_amount") { it.unsafeCast<ProductDeliveryFlat>().wrapped_amount }
    defGet(pdfProto, "wrapped_amount_return") { it.unsafeCast<ProductDeliveryFlat>().wrapped_amount_return }
    defGet(pdfProto, "conversion_ratio") { it.unsafeCast<ProductDeliveryFlat>().conversion_ratio }
    defGet(pdfProto, "productId") { it.unsafeCast<ProductDeliveryFlat>().productId }
    defGet(pdfProto, "entId") { it.unsafeCast<ProductDeliveryFlat>().entId }

    // TaxNoteSum (TAX_NOTE_EXPLAIN -> tax note product import)
    val tnsProto = protoOf(TaxNoteSum::class.js)
    defGet(tnsProto, "id") { it.unsafeCast<TaxNoteSum>().id }
    defGet(tnsProto, "name") { it.unsafeCast<TaxNoteSum>().name }
    defGet(tnsProto, "amount") { it.unsafeCast<TaxNoteSum>().amount }
    defGet(tnsProto, "returns") { it.unsafeCast<TaxNoteSum>().returns }
    defGet(tnsProto, "price") { it.unsafeCast<TaxNoteSum>().price }
    defGet(tnsProto, "discount") { it.unsafeCast<TaxNoteSum>().discount }
    defGet(tnsProto, "total") { it.unsafeCast<TaxNoteSum>().total }
    defGet(tnsProto, "priceMin") { it.unsafeCast<TaxNoteSum>().priceMin }
    defGet(tnsProto, "priceMax") { it.unsafeCast<TaxNoteSum>().priceMax }
    defGet(tnsProto, "priceBefore") { it.unsafeCast<TaxNoteSum>().priceBefore }
    defGet(tnsProto, "priceBeforeMin") { it.unsafeCast<TaxNoteSum>().priceBeforeMin }
    defGet(tnsProto, "priceBeforeMax") { it.unsafeCast<TaxNoteSum>().priceBeforeMax }
    defGet(tnsProto, "totalBefore") { it.unsafeCast<TaxNoteSum>().totalBefore }

    // TaxNoteMassHolder (PREPARE_MASS_TAX_NOTE -> all-tax-note-send)
    val tnmhProto = protoOf(TaxNoteMassHolder::class.js)
    defGet(tnmhProto, "client_id") { it.unsafeCast<TaxNoteMassHolder>().client_id }
    defGet(tnmhProto, "notes") { it.unsafeCast<TaxNoteMassHolder>().notes }
    defGet(tnmhProto, "taxNotes") { it.unsafeCast<TaxNoteMassHolder>().taxNotes }
    defGet(tnmhProto, "openSum") { it.unsafeCast<TaxNoteMassHolder>().openSum }
    defGet(tnmhProto, "openTax") { it.unsafeCast<TaxNoteMassHolder>().openTax }
    defGet(tnmhProto, "totalSum") { it.unsafeCast<TaxNoteMassHolder>().totalSum }
    defGet(tnmhProto, "date") { it.unsafeCast<TaxNoteMassHolder>().date }
    defGet(tnmhProto, "totalTaxTn") { it.unsafeCast<TaxNoteMassHolder>().totalTaxTn }
    defGet(tnmhProto, "totalNotes") { it.unsafeCast<TaxNoteMassHolder>().totalNotes }
    defGet(tnmhProto, "totalTax") { it.unsafeCast<TaxNoteMassHolder>().totalTax }

    // DebtWall fields used by datasheet / debt helpers (methods @myName'd; props not)
    val dwProto = protoOf(DebtWall::class.js)
    defGet(dwProto, "moneyOut") { it.unsafeCast<DebtWall>().moneyOut }
    defGet(dwProto, "moneyIn") { it.unsafeCast<DebtWall>().moneyIn }

    // ClientVisitHolder (GET_CLIENT_VISIT_DISTINCT -> orders "ordering customers" filter)
    val cvVisitProto = protoOf(ClientVisitHolder::class.js)
    defGet(cvVisitProto, "ent_id") { it.unsafeCast<ClientVisitHolder>().ent_id }
    defGet(cvVisitProto, "doc_type") { it.unsafeCast<ClientVisitHolder>().doc_type }
    defGet(cvVisitProto, "counter") { it.unsafeCast<ClientVisitHolder>().counter }
}

/** הפקדות screen: Deposit / DepositLine rows and the bank accounts on the hashavshevet conf. */
private fun installDepositShims() {
    val depProto = protoOf(Deposit::class.js)
    defGet(depProto, "id") { it.unsafeCast<Deposit>().id }
    defGet(depProto, "number") { it.unsafeCast<Deposit>().number }
    defGet(depProto, "deposit_date") { it.unsafeCast<Deposit>().deposit_date }
    defGet(depProto, "bank_account_id") { it.unsafeCast<Deposit>().bank_account_id }
    defGet(depProto, "source_type") { it.unsafeCast<Deposit>().source_type }
    defGet(depProto, "total") { it.unsafeCast<Deposit>().total }
    defGet(depProto, "lines_count") { it.unsafeCast<Deposit>().lines_count }
    defGet(depProto, "status") { it.unsafeCast<Deposit>().status }
    defGet(depProto, "exported_at") { it.unsafeCast<Deposit>().exported_at }
    defGet(depProto, "notes") { it.unsafeCast<Deposit>().notes }
    defGet(depProto, "user_name") { it.unsafeCast<Deposit>().user_name }
    defGet(depProto, "created_at") { it.unsafeCast<Deposit>().created_at }
    // Derived class-body property: not a constructor field, so it gets no
    // accessor of its own unless shimmed here.
    defGet(depProto, "lines") { it.unsafeCast<Deposit>().lines }
    defMethod0(depProto, "getSourceLabel") { it.unsafeCast<Deposit>().getSourceLabel() }
    defMethod0(depProto, "getLinesTotal") { it.unsafeCast<Deposit>().getLinesTotal() }
    defMethod0(depProto, "isActive") { it.unsafeCast<Deposit>().isActive() }

    val dlProto = protoOf(DepositLine::class.js)
    defGet(dlProto, "doc_type") { it.unsafeCast<DepositLine>().doc_type }
    defGet(dlProto, "doc_id") { it.unsafeCast<DepositLine>().doc_id }
    defGet(dlProto, "payment_index") { it.unsafeCast<DepositLine>().payment_index }
    defGet(dlProto, "payment_inner_id") { it.unsafeCast<DepositLine>().payment_inner_id }
    defGet(dlProto, "sup") { it.unsafeCast<DepositLine>().sup }
    defGet(dlProto, "pay_method") { it.unsafeCast<DepositLine>().pay_method }
    defGet(dlProto, "value") { it.unsafeCast<DepositLine>().value }
    defGet(dlProto, "check_number") { it.unsafeCast<DepositLine>().check_number }
    defGet(dlProto, "check_date") { it.unsafeCast<DepositLine>().check_date }
    defGet(dlProto, "bank_id") { it.unsafeCast<DepositLine>().bank_id }
    defGet(dlProto, "bank_branch") { it.unsafeCast<DepositLine>().bank_branch }
    defGet(dlProto, "bank_number") { it.unsafeCast<DepositLine>().bank_number }
    defGet(dlProto, "client_id") { it.unsafeCast<DepositLine>().client_id }
    defGet(dlProto, "client_name") { it.unsafeCast<DepositLine>().client_name }
    defGet(dlProto, "document_date") { it.unsafeCast<DepositLine>().document_date }
    defGet(dlProto, "payMethod") { it.unsafeCast<DepositLine>().payMethod }
    defMethod0(dlProto, "isCheck") { it.unsafeCast<DepositLine>().isCheck() }

    val baProto = protoOf(BankAccountConf::class.js)
    defGet(baProto, "id") { it.unsafeCast<BankAccountConf>().id }
    defGet(baProto, "name") { it.unsafeCast<BankAccountConf>().name }
    defGet(baProto, "bank_id") { it.unsafeCast<BankAccountConf>().bank_id }
    defGet(baProto, "branch") { it.unsafeCast<BankAccountConf>().branch }
    defGet(baProto, "account") { it.unsafeCast<BankAccountConf>().account }
    defGet(baProto, "hash_account") { it.unsafeCast<BankAccountConf>().hash_account }
    defGet(baProto, "default") { it.unsafeCast<BankAccountConf>().default }
    defMethod0(baProto, "getDisplayName") { it.unsafeCast<BankAccountConf>().getDisplayName() }

    val hashProto = protoOf(HashavshevetS::class.js)
    defGet(hashProto, "deposit_move") { it.unsafeCast<HashavshevetS>().deposit_move }
    defGet(hashProto, "bank_accounts") { it.unsafeCast<HashavshevetS>().bank_accounts }
    defGet(hashProto, "bankAccounts") { it.unsafeCast<HashavshevetS>().bankAccounts }
    defMethod0(hashProto, "depositsConfigured") { it.unsafeCast<HashavshevetS>().depositsConfigured() }
    defGet(hashProto, "expense_with") { it.unsafeCast<HashavshevetS>().expense_with }
    defGet(hashProto, "expense_without") { it.unsafeCast<HashavshevetS>().expense_without }
    defGet(hashProto, "maam_in") { it.unsafeCast<HashavshevetS>().maam_in }
    defGet(hashProto, "sup_tax_move") { it.unsafeCast<HashavshevetS>().sup_tax_move }
    defGet(hashProto, "sup_tax_cancel_move") { it.unsafeCast<HashavshevetS>().sup_tax_cancel_move }
    defGet(hashProto, "sup_pay_move") { it.unsafeCast<HashavshevetS>().sup_pay_move }
    defMethod0(hashProto, "supplierApConfigured") { it.unsafeCast<HashavshevetS>().supplierApConfigured() }
}

private fun installRawPriceShims() {
    val proto = protoOf(RawPrice::class.js)
    defGet(proto, "id") { it.unsafeCast<RawPrice>().id }
    defGet(proto, "product_id") { it.unsafeCast<RawPrice>().product_id }
    defGet(proto, "price") { it.unsafeCast<RawPrice>().price }
    defGet(proto, "date") { it.unsafeCast<RawPrice>().date }
    defGet(proto, "discount") { it.unsafeCast<RawPrice>().discount }
    defGet(proto, "master") { it.unsafeCast<RawPrice>().master }
    defGet(proto, "agent") { it.unsafeCast<RawPrice>().agent }
    defGet(proto, "action_time") { it.unsafeCast<RawPrice>().action_time }
}

private fun installDbShims() {
    // Must not reuse the Kotlin property name: defGet installs a getter-only
    // accessor on the same object, so a matching name would make Kotlin's own
    // writes to the property throw in strict mode.
    defGet(JsDB.asDynamic(), "fieldPhotos") { self ->
        val list = self.unsafeCast<NKDBInterface>().lastLoadedFieldPhotos
        val arr = js("[]")
        for (bytes in list) {
            arr.push(bytes)
        }
        arr
    }
    defMethod2(JsDB.asDynamic(), "clientProductReturnPct") { self, clientId, productId ->
        self.unsafeCast<NKDBInterface>().clientProductReturnPct(
            (clientId as Number).toInt(),
            (productId as Number).toInt(),
        )
    }
    defMethod2(JsDB.asDynamic(), "clientProductAllowsReturns") { self, clientId, productId ->
        self.unsafeCast<NKDBInterface>().clientProductAllowsReturns(
            (clientId as Number).toInt(),
            (productId as Number).toInt(),
        )
    }
}

fun installInteropShims() {
    installStdlibShims()
    installDocShims()
    installEntityClassShims()
    installProductShims()
    installProductDeliveryShims()
    installMiscShims()
    installDepositShims()
    installRawPriceShims()
    installDbShims()
}
